import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RichTextEditor from './RichTextEditor';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  X,
  FileText,
  Upload,
  Calendar,
  FileType,
  Loader2,
  Search,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Award,
  CreditCard,
  Download,
  ChevronLeft,
  RotateCcw,
  AlertTriangle,
  MessageSquare,
  UserCheck,
  CalendarDays,
  Send,
  Trash2,
  Edit2,
  TrendingUp,
  Plus,
  Building2,
  FolderOpen,
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  Timer,
} from 'lucide-react';
import DateFormatter from './DateFormatter';
import { formatPositionsDisplay } from '../services/positionAPIs';
import {
  createOvertime,
  getOvertimeByEmployeeId,
  updateOvertime,
  deleteOvertime
} from '../services/overtimeAPI';
import './ViewProfile.css';
import AbsenceRecordsModal from './AbsenceRecordsModal';
import {
  getEmployeeById,
  getEmployeeImage,
} from '../services/HRstaff';
import { getContractByEmployeeId, getContractsByEmployeeId } from '../services/ContractAPI';
import {
  createContractRenewalFeedback,
  getAllContractRenewalFeedbacks,
  updateContractRenewalFeedback,
  deleteContractRenewalFeedback
} from '../services/contractRenewalFeedbackAPIs';
import { getEmployeeLeaves } from '../services/leavesAPIs';
import { getAnnualLeaveBalanceByYear } from '../services/leaveBalanceAPIs';
import { getEmployeeAbsences } from '../services/absenceAPIs';
import api from '../api';
import { API_URL } from '../config';
import { getBonusesByEmployeeId, createBonus, updateBonus, deleteBonus } from '../services/bonusesAPI';
import {
  getEmployeeLatenessSummaries,
  getEmployeeSicknessSummaries,
  getEmployeePersonalCommitmentsSummaries,
} from '../services/absenceAPIs';
import {
  getEmployeeRequests,
  approveRequest,
  rejectRequest,
} from '../services/requestAPIs';
import {
  calculateDaysWorked,
  calculateMonthsWorked,
  calculateTotalMonths,
  formatMonthsWorked,
} from './dateCalculations';
import { getAllDocuments, createDocument, deleteDocument } from '../services/documentAPIs';
import EmployeeAttendanceReportDialog from '../components/DViews/EmployeeAttendance';
import DocumentDetailsModal from './DViews/DocDetailsModal';
import { getEmployeeNotes, addEmployeeNote, updateEmployeeNote, deleteEmployeeNote } from '../services/NoteAPIs';
import { getEmployeeCareers, createEmployeeCareer, updateEmployeeCareer, deleteEmployeeCareer } from '../services/careerAPIs';
import {
  getAbsenceViolationByAbsenceId,
  createAbsenceViolation,
  updateAbsenceViolation,
} from '../services/absenceviolationAPIs';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { IconEyeEdit } from '@tabler/icons-react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
// Format date to DD-MM-YYYY
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (err) {
    return 'Invalid Date';
  }
};

const calculateAge = (dob) => {
  if (!dob) return 'N/A';
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return 'N/A';
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 0 ? age : 'N/A';
};

// --- NEW CONSTANT: Violation Types ---
const VIOLATION_TYPES = [
  "Violation of Rules",
  "Behavior",
  "Work Performance",
  "Break Compliance",
  "Attendance and Punctuality",
  "Unauthorized Absence",
  "Early Departure",
  "Insubordination",
  "Harassment or Discrimination",
  "Use of Mobile Phone During Work",
  "Dress Code Violation",
  "Unprofessional Communication",
  "Disruption of Workplace",
  "Confidentiality Breach",
  "New Arrivals Feedback"
];

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const ViewProfile = ({ onClose = null }) => {
  const { id } = useParams();

  // Reactive dark-mode detection
  const [isDark, setIsDark] = useState(() => document.body.classList.contains('dark'));
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.body.classList.contains('dark'))
    );
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Dark-mode color helpers
  const dm = {
    card: isDark ? '#1e293b' : 'var(--card-bg, white)',
    surface: isDark ? '#0f172a' : '#F3F4F6',
    border: isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB',
    textPrimary: isDark ? '#f1f5f9' : '#111827',
    textSecondary: isDark ? '#94a3b8' : '#6B7280',
    textMuted: isDark ? '#64748b' : '#9CA3AF',
    btnInactiveBg: isDark ? 'rgba(255,255,255,0.06)' : 'transparent',
    btnInactiveColor: isDark ? '#94a3b8' : '#6B7280',
    progressTrack: isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB',
    yearBtnBg: isDark ? '#1e293b' : 'white',
    yearBtnColor: isDark ? '#e2e8f0' : '#374151',
  };
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [contract, setContract] = useState(null);
  const [notes, setNotes] = useState([]);

  // --- NEW FEEDBACK STATE (Replaces old 'newNote') ---
  const [feedbackForm, setFeedbackForm] = useState({
    date: new Date().toISOString().split('T')[0],
    violationType: VIOLATION_TYPES[0],
    description: '',
    managerName: '',
    employeeReaction: '',
    employeeAgreement: '',
    noteType: 'NEGATIVE'
  });

  const [editingNote, setEditingNote] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [activeTab, setActiveTab] = useState('attendance');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isProcessingRequest, setIsProcessingRequest] = useState(false);

  // --- CONTRACT RENEWAL FEEDBACK STATES ---
  const [contractFeedbacks, setContractFeedbacks] = useState([]);
  const [empContracts, setEmpContracts] = useState([]);
  const [contractFeedbacksLoaded, setContractFeedbacksLoaded] = useState(false);
  const [contractRenewalForm, setContractRenewalForm] = useState({
    contractId: '',
    managerFeedback: '',
    projectManagerFeedback: '',
    hrFeedback: '',
    actionRequired: '',
    numberOfExtensions: '',
    finalExtensionPossible: ''
  });
  const [editingContractFeedbackId, setEditingContractFeedbackId] = useState(null);
  const [editContractForm, setEditContractForm] = useState({
    contractId: '',
    managerFeedback: '',
    projectManagerFeedback: '',
    hrFeedback: '',
    actionRequired: '',
    numberOfExtensions: '',
    finalExtensionPossible: ''
  });
  const [isSavingContractFeedback, setIsSavingContractFeedback] = useState(false);

  // --- BONUSES TAB STATE ---
  const [bonuses, setBonuses] = useState([]);
  const [bonusesLoaded, setBonusesLoaded] = useState(false);
  const [bonusForm, setBonusForm] = useState({ description: '', type: 'NOTICE_PAY', amount: '' });
  const [editingBonusId, setEditingBonusId] = useState(null);
  const [editBonusForm, setEditBonusForm] = useState({ description: '', type: 'NOTICE_PAY', amount: '' });
  const [isSavingBonus, setIsSavingBonus] = useState(false);

  // --- OVERTIME TAB STATE ---
  const [overtimes, setOvertimes] = useState([]);
  const [overtimesLoaded, setOvertimesLoaded] = useState(false);
  const [overtimeForm, setOvertimeForm] = useState({ date: null, hours: '', description: '' });
  const [editingOvertimeId, setEditingOvertimeId] = useState(null);
  const [editOvertimeForm, setEditOvertimeForm] = useState({ date: null, hours: '', description: '' });
  const [isSavingOvertime, setIsSavingOvertime] = useState(false);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Year state
  const [latenessRate, setLatenessRate] = useState(0);
  const [latenessOver10Rate, setLatenessOver10Rate] = useState(0);
  const [sicknessRate, setSicknessRate] = useState(0);
  const [personalRate, setPersonalRate] = useState(0);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('All');
  const [docSearchTerm, setDocSearchTerm] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [selectedAbsenceType, setSelectedAbsenceType] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    type: 'Contract',
    description: '',
    file: null,
  });
  const [uploading, setUploading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState(null);
  const [docError, setDocError] = useState(null);
  const [docSuccess, setDocSuccess] = useState(null);
  const [vacationDaysLeft, setVacationDaysLeft] = useState(null);
  const [sickDays, setSickDays] = useState(0);
  const [personalDays, setPersonalDays] = useState(0);
  const [latenessDays, setLatenessDays] = useState(0);
  const [suspensionDays, setSuspensionDays] = useState(0);
  const [unpaidDays, setUnpaidDays] = useState(0);
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  // --- CAREER TAB STATE ---
  const CAREER_PROJECT_OPTIONS = [
    'N/A', 'MM', 'LC', 'IT', 'FF', 'FL', 'MKT', 'HA', 'CS', 'HR', 'HR-S',
    'H2D', 'WLF', 'HBL', 'MC', 'TEDS', 'QR', 'MA', 'PL', 'WAL',
    'SIPI', 'Lineo', 'CB', 'UTB', 'AP', 'AXP', 'ENTRPNR',
  ];
  const CAREER_POSITION_OPTIONS = [
    'Account Manager', 'Bookkeeper', 'Client Adviser', 'Customer Service',
    'Developer', 'Director', 'Facility', 'HR', 'HR Manager', 'IT Professional',
    'Manager', 'Marketing', 'Personal Assistance', 'Planner', 'Project Manager',
    'Quality Reviewer', 'Software Engineer', 'HQ',
  ];
  const [careers, setCareers] = useState([]);
  const [careersLoaded, setCareersLoaded] = useState(false);
  const [showCareerForm, setShowCareerForm] = useState(false);
  const [editingCareer, setEditingCareer] = useState(null);
  const [allDepartments, setAllDepartments] = useState([]);
  const [careerDropdownsLoaded, setCareerDropdownsLoaded] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Internship', 'Contract', 'Freelance', 'Consultant'];
  const emptyCareerForm = {
    positionTitle: '',
    department: '',
    startDate: '',
    endDate: '',
    employmentType: 'Full-time',
    description: '',
    projectNames: '',
  };
  const [careerForm, setCareerForm] = useState(emptyCareerForm);

  // --- NEW STATE FOR FEEDBACK SECTION ---
  const [feedbackTab, setFeedbackTab] = useState('violations'); // 'violations' | 'attendance'
  const [feedbackYear, setFeedbackYear] = useState(new Date().getFullYear());
  const [feedbackMonth, setFeedbackMonth] = useState('All');
  const [absenceHistory, setAbsenceHistory] = useState([]);

  // --- ASSESSMENT MODAL STATE ---
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [selectedAbsenceForAssessment, setSelectedAbsenceForAssessment] = useState(null);
  const [assessmentForm, setAssessmentForm] = useState({
    violationDescription: '',
    managerReview: '',
    employeeReaction: '',
    employeeAgreement: ''
  });
  // Map: absenceId -> violation object (or null if none exists)
  const [absenceViolationsMap, setAbsenceViolationsMap] = useState({});
  const [violationsLoading, setViolationsLoading] = useState(false);
  const [feedbackSortOrder, setFeedbackSortOrder] = useState('desc'); // 'desc' = newest first

  // Calculate violation type counts
  const violationTypeCounts = useMemo(() => {
    const counts = {};
    VIOLATION_TYPES.forEach(type => {
      counts[type] = { negative: 0, positive: 0 };
    });
    notes.forEach(note => {
      if (note.violationType && counts.hasOwnProperty(note.violationType)) {
        if (!note.noteType || note.noteType === 'NEGATIVE') {
          counts[note.violationType].negative++;
        } else if (note.noteType === 'POSITIVE') {
          counts[note.violationType].positive++;
        }
      }
    });
    return counts;
  }, [notes]);

  // Profile image effect
  useEffect(() => {
    const loadMainProfileImage = async () => {
      if (employee && employee.hasImage) {
        try {
          const imageUrl = await getEmployeeImage(employee.id);
          setProfileImageUrl(imageUrl);
        } catch (error) {
          console.error('Failed to load main profile image:', error);
          setProfileImageUrl(null);
        }
      } else {
        setProfileImageUrl(null);
      }
    };
    loadMainProfileImage();
    return () => {
      if (profileImageUrl) URL.revokeObjectURL(profileImageUrl);
    };
  }, [employee?.id, employee?.hasImage]);

  // Other data effects
  useEffect(() => {
    const fetchEmployeeData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const employeeData = await getEmployeeById(id);
        let requests = [];
        try {
          requests = await getEmployeeRequests(id);
          requests = requests.map((req) => ({
            id: req.requestId || req.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
            type: req.type || 'UNKNOWN',
            status: req.status || 'PENDING',
            details: req.details || req.description || 'N/A',
            date: req.date || req.submissionDate || req.createdAt || new Date(),
            document: req.document || null,
          }));
        } catch (requestError) {
          console.error('Error fetching employee requests:', requestError);
          requests = [];
        }
        const joinDate = employeeData.joinDate || employeeData.dateOfJoining;
        if (!joinDate) {
          throw new Error('Join date is required for attendance calculation');
        }
        const totalMonths = calculateTotalMonths(joinDate);
        const monthsWorked = calculateMonthsWorked(joinDate);
        const daysWorked = calculateDaysWorked(joinDate);
        const calculateWorkDays = (startDate) => {
          const start = new Date(startDate);
          const end = new Date();
          let count = 0;
          let current = new Date(start);
          while (current <= end) {
            const day = current.getDay();
            if (day !== 0 && day !== 6) count++;
            current.setDate(current.getDate() + 1);
          }
          return count;
        };
        const totalWorkDaysEmployed = calculateWorkDays(joinDate);
        const actualWorkedDays = totalWorkDaysEmployed;
        const transformedEmployee = {
          id: employeeData.employeeId || id,
          name: `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim() || 'N/A',
          firstName: employeeData.firstName || '',
          lastName: employeeData.lastName || '',
          hasImage: !!employeeData.image,
          position: formatPositionsDisplay(employeeData),
          status: employeeData.status || 'Active',
          joinDate: joinDate,
          company: employeeData.company || 'N/A',
          personalInfo: {
            location: employeeData.officeName || employeeData.office?.name || employeeData.office || 'Unknown',
            office: employeeData.officeName || employeeData.office?.name || employeeData.office || 'N/A',
          },
          user: {
            email: employeeData.email || employeeData.user?.email || 'N/A',
            role: employeeData.role || employeeData.user?.role || 'N/A',
          },
          cnss: employeeData.cnss || 'N/A',
          cin: employeeData.cin || 'N/A',
          gender: employeeData.gender || 'N/A',
          phone: employeeData.phone || 'N/A',
          dob: employeeData.dob || null,
          diploma: employeeData.diploma || 'N/A',
          office: employeeData.officeName || employeeData.office?.name || 'N/A',
          requests: requests,
          timeOverview: {
            totalMonths: Math.round(monthsWorked),
            workedMonths: Math.round(monthsWorked),
            workedDays: daysWorked,
            actualWorkedDays: actualWorkedDays,
            totalDaysEmployed: totalWorkDaysEmployed,
            formattedDuration: formatMonthsWorked(joinDate),
          },
          notes: employeeData.notes || '',
        };
        setEmployee(transformedEmployee);
        try {
          const notesData = await getEmployeeNotes(id);
          setNotes(notesData);
        } catch (notesError) {
          console.error('Error fetching notes:', notesError);
          setNotes([]);
        }
        try {
          const employeeContract = await getContractByEmployeeId(id);



          setContract(employeeContract || null);
        } catch (contractError) {
          console.error('Error fetching contract:', contractError);
          setContract(null);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load employee data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    if (id) fetchEmployeeData();
  }, [id]);

  useEffect(() => {
    const fetchSuspensionData = async () => {
      if (!id) return;
      try {
        const absences = await getEmployeeAbsences(id, selectedYear);
        const suspensionRecords = absences.filter(
          (record) => record.ty_pe?.toUpperCase() === 'SUSPENSION'
        );
        const totalSuspensionDays = suspensionRecords.reduce(
          (sum, record) => sum + (record.days || record.durationInDays || 0),
          0
        );
        setSuspensionDays(totalSuspensionDays);
      } catch (err) {
        console.error('Failed to fetch suspension data:', err);
        setSuspensionDays(0);
      }
    };
    fetchSuspensionData();
  }, [id, selectedYear]);

  // ✅ Updated Rate Fetching to depend on Selected Year
  // ✅ Updated Rate Fetching to depend on Selected Year
  useEffect(() => {
    const fetchAbsenceRates = async () => {
      if (!id) return;
      setRatesLoading(true);
      try {
        const latenessData = await getEmployeeLatenessSummaries(selectedYear);
        const latenessEmp = latenessData.find((emp) => emp.employeeId === id);

        const latenessOver10Count = latenessEmp ? (latenessEmp.latenessOver10Count || 0) : 0;
        const totalWorkedDaysLateness = latenessEmp ? (latenessEmp.totalWorkedDays || 0) : 0;
        const over10Rate = totalWorkedDaysLateness > 0 ? parseFloat(((latenessOver10Count / totalWorkedDaysLateness) * 100).toFixed(2)) : 0;

        setLatenessRate(latenessEmp ? parseFloat(latenessEmp.absenceRate.toFixed(2)) : 0);
        setLatenessOver10Rate(over10Rate);

        const sicknessData = await getEmployeeSicknessSummaries(selectedYear);
        const sicknessEmp = sicknessData.find((emp) => emp.employeeId === id);
        setSicknessRate(sicknessEmp ? parseFloat(sicknessEmp.absenceRate.toFixed(2)) : 0);

        const personalData = await getEmployeePersonalCommitmentsSummaries(selectedYear);
        const personalEmp = personalData.find((emp) => emp.employeeId === id);
        setPersonalRate(personalEmp ? parseFloat(personalEmp.absenceRate.toFixed(2)) : 0);
      } catch (err) {
        console.error('Failed to fetch absence rates:', err);
        setLatenessRate(0);
        setSicknessRate(0);
        setPersonalRate(0);
      } finally {
        setRatesLoading(false);
      }
    };
    fetchAbsenceRates();
  }, [id, selectedYear]); // ✅ Add selectedYear dependency

  useEffect(() => {
    const fetchLeaveBalance = async () => {
      if (!employee?.id) return;
      try {
        // Get all year balances from backend
        const balances = await getAnnualLeaveBalanceByYear(employee.id);
        const yearBalance = balances?.[selectedYear] ?? 0;
        setVacationDaysLeft(yearBalance);

      } catch (err) {
        console.error('Failed to fetch leave balance:', err);
        setVacationDaysLeft(0);
      }
    };
    fetchLeaveBalance();
  }, [employee?.id, selectedYear]);

  useEffect(() => {
    const fetchAbsenceData = async () => {
      if (!id) return;
      try {
        const absences = await getEmployeeAbsences(id, selectedYear);

        const sicknessData = absences.filter((record) => record.ty_pe?.toUpperCase() === 'SICKNESS');
        const totalSick = sicknessData.length;
        setSickDays(totalSick);

        const personalData = absences.filter(
          (record) =>
            record.ty_pe?.toUpperCase() === 'PERSONAL_COMMITMENT' || record.ty_pe?.toUpperCase() === 'PERSONAL'
        );
        const totalPersonal = personalData.length;
        setPersonalDays(totalPersonal);

        const latenessData = absences.filter((record) => record.ty_pe?.toUpperCase() === 'LATENESS');
        const totalLateness = latenessData.length;
        setLatenessDays(totalLateness);
      } catch (err) {
        console.error('Failed to fetch absence counts:', err);
        setSickDays(0);
        setPersonalDays(0);
        setLatenessDays(0);
      }

      // Fetch unpaid leave data separately (from leave system, not absence system)
      try {
        const allLeaves = await getEmployeeLeaves(id);

        const unpaidLeaves = allLeaves.filter(leave => {
          if (leave.status?.toUpperCase() !== 'APPROVED') return false;
          if (leave.type !== 'UNPAID_LEAVE') return false;
          const leaveYear = new Date(leave.startDate).getFullYear();
          return leaveYear === selectedYear;
        });
        const totalUnpaid = unpaidLeaves.reduce((sum, leave) => sum + (leave.days || 0), 0);
        setUnpaidDays(totalUnpaid);
      } catch (leaveErr) {
        console.error('Failed to fetch unpaid leave data:', leaveErr);
        setUnpaidDays(0);
      }
    };
    fetchAbsenceData();
    fetchAbsenceData();
  }, [id, selectedYear]);

  // --- FETCH ABSENCE HISTORY + VIOLATIONS FOR FEEDBACK TAB ---
  const loadViolationsForAbsences = async (absences) => {
    if (!absences || absences.length === 0) return;
    setViolationsLoading(true);
    const map = {};
    await Promise.all(
      absences.map(async (record) => {
        if (!record.id) return;
        try {
          const violation = await getAbsenceViolationByAbsenceId(record.id);
          map[record.id] = violation || null;
        } catch (err) {
          // 404 or no violation means none exists yet
          map[record.id] = null;
        }
      })
    );
    setAbsenceViolationsMap(map);
    setViolationsLoading(false);
  };

  useEffect(() => {
    const fetchFeedbackAbsences = async () => {
      if (!id) return;
      try {
        const data = await getEmployeeAbsences(id, feedbackYear);
        setAbsenceHistory(data || []);
        await loadViolationsForAbsences(data || []);
      } catch (err) {
        console.error('Failed to fetch absence history for feedback:', err);
        setAbsenceHistory([]);
      }
    };
    fetchFeedbackAbsences();
  }, [id, feedbackYear]);

  // --- BONUSES LOGIC ---
  useEffect(() => {
    if (employee?.id && activeTab === 'bonuses' && !bonusesLoaded) {
      getBonusesByEmployeeId(employee.id)
        .then((data) => {
          setBonuses(data || []);
          setBonusesLoaded(true);
        })
        .catch((err) => console.error('Failed to load bonuses:', err));
    }
  }, [employee?.id, activeTab, bonusesLoaded]);

  const handleSubmitBonus = async (e) => {
    e.preventDefault();
    if (!employee?.id) return;
    setIsSavingBonus(true);
    try {
      const payload = {
        employeeId: employee.id,
        description: bonusForm.description,
        type: bonusForm.type,
        amount: parseFloat(bonusForm.amount)
      };
      const created = await createBonus(payload);
      setBonuses((prev) => [...prev, created]);
      setBonusForm({ description: '', type: 'NOTICE_PAY', amount: '' });
      alert('Bonus added successfully!');
    } catch (err) {
      console.error('Failed to create bonus:', err);
      alert('Failed to add bonus.');
    } finally {
      setIsSavingBonus(false);
    }
  };

  const handleUpdateBonus = async () => {
    if (!editingBonusId) return;
    setIsSavingBonus(true);
    try {
      const payload = {
        description: editBonusForm.description,
        type: editBonusForm.type,
        amount: parseFloat(editBonusForm.amount)
      };
      const updated = await updateBonus(editingBonusId, payload);
      setBonuses((prev) => prev.map((b) => (b.id === editingBonusId ? updated : b)));
      setEditingBonusId(null);
      setEditBonusForm({ description: '', type: 'NOTICE_PAY', amount: '' });
      alert('Bonus updated successfully!');
    } catch (err) {
      console.error('Failed to update bonus:', err);
      alert('Failed to update bonus.');
    } finally {
      setIsSavingBonus(false);
    }
  };

  const handleDeleteBonus = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bonus?')) return;
    try {
      await deleteBonus(id);
      setBonuses((prev) => prev.filter((b) => b.id !== id));
      alert('Bonus deleted successfully!');
    } catch (err) {
      console.error('Failed to delete bonus:', err);
      alert('Failed to delete bonus.');
    }
  };

  // --- OVERTIME LOGIC ---
  useEffect(() => {
    if (employee?.id && activeTab === 'overtime' && !overtimesLoaded) {
      getOvertimeByEmployeeId(employee.id)
        .then((data) => {
          setOvertimes(data || []);
          setOvertimesLoaded(true);
        })
        .catch((err) => console.error('Failed to load overtime records:', err));
    }
  }, [employee?.id, activeTab, overtimesLoaded]);

  const formatDatetimeLocal = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const pad = (n) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const formatDateTimeDisplay = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    try {
      const date = new Date(dateTimeString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (e) {
      return 'N/A';
    }
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  };

  const handleSubmitOvertime = async (e) => {
    e.preventDefault();
    if (!employee?.id) return;
    if (!overtimeForm.date || !overtimeForm.hours) {
      alert("Please fill in both Date and Hours.");
      return;
    }
    if (parseFloat(overtimeForm.hours) <= 0) {
      alert("Hours must be greater than 0.");
      return;
    }
    setIsSavingOvertime(true);
    try {
      const baseDate = new Date(overtimeForm.date);
      baseDate.setHours(9, 0, 0, 0);
      const endTime = new Date(baseDate.getTime() + parseFloat(overtimeForm.hours) * 60 * 60 * 1000);

      const payload = {
        employeeId: employee.id,
        date: overtimeForm.date,
        hours: parseFloat(overtimeForm.hours),
        description: overtimeForm.description,
        confirmed: null
      };
      const created = await createOvertime(payload);
      setOvertimes((prev) => [created, ...prev]);
      setOvertimeForm({ date: null, hours: '', description: '' });
      alert('Overtime record added successfully!');
    } catch (err) {
      console.error('Failed to create overtime:', err);
      alert('Failed to add overtime record.');
    } finally {
      setIsSavingOvertime(false);
    }
  };

  const handleUpdateOvertime = async () => {
    if (!editingOvertimeId) return;
    if (!editOvertimeForm.date || !editOvertimeForm.hours) {
      alert("Please fill in both Date and Hours.");
      return;
    }
    if (parseFloat(editOvertimeForm.hours) <= 0) {
      alert("Hours must be greater than 0.");
      return;
    }
    setIsSavingOvertime(true);
    try {
      const baseDate = new Date(editOvertimeForm.date);
      baseDate.setHours(9, 0, 0, 0);
      const endTime = new Date(baseDate.getTime() + parseFloat(editOvertimeForm.hours) * 60 * 60 * 1000);

      const payload = {
        date: editOvertimeForm.date,
        hours: parseFloat(editOvertimeForm.hours),
        description: editOvertimeForm.description
      };
      const updated = await updateOvertime(editingOvertimeId, payload);
      setOvertimes((prev) => prev.map((o) => (o.id === editingOvertimeId ? updated : o)));
      setEditingOvertimeId(null);
      setEditOvertimeForm({ date: null, hours: '', description: '' });
      alert('Overtime record updated successfully!');
    } catch (err) {
      console.error('Failed to update overtime:', err);
      alert('Failed to update overtime record.');
    } finally {
      setIsSavingOvertime(false);
    }
  };

  const handleDeleteOvertime = async (id) => {
    if (!window.confirm('Are you sure you want to delete this overtime record?')) return;
    try {
      await deleteOvertime(id);
      setOvertimes((prev) => prev.filter((o) => o.id !== id));
      alert('Overtime record deleted successfully!');
    } catch (err) {
      console.error('Failed to delete overtime:', err);
      alert('Failed to delete overtime record.');
    }
  };

  const fetchEmployeeDocuments = async () => {
    try {
      const allDocs = await getAllDocuments();
      const employeeDocs = allDocs.filter((doc) => doc.employee?.employeeId === id);
      setDocuments(employeeDocs);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setDocError('Failed to load documents.');
    }
  };

  useEffect(() => {
    if (docError || docSuccess) {
      const timer = setTimeout(() => {
        setDocError(null);
        setDocSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [docError, docSuccess]);

  useEffect(() => {
    if (employee?.id) {
      fetchEmployeeDocuments();
    }
  }, [employee?.id]);

  const handleUploadChange = (e) => {
    const { name, value, files } = e.target;
    setUploadForm((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleUploadSubmit = async () => {
    if (!uploadForm.description || !uploadForm.file) {
      setDocError('Please fill all required fields and select a file.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      const documentData = {
        employee: { employeeId: id },
        type: uploadForm.type,
        description: uploadForm.description,
      };
      formData.append('document', new Blob([JSON.stringify(documentData)], { type: 'application/json' }));
      formData.append('file', uploadForm.file);
      const newDocument = await createDocument(formData);
      setDocuments((prev) => [...prev, newDocument]);
      setDocSuccess('Document uploaded successfully!');
      setUploadForm({ type: 'Contract', description: '', file: null });
      setShowUploadForm(false);
    } catch (err) {
      setDocError('Failed to upload document. Please try again.');
      console.error('Error uploading document:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!deleteDocId) return;
    try {
      await deleteDocument(deleteDocId);
      setDocuments((prev) => prev.filter((doc) => doc.id !== deleteDocId));
      setDocSuccess('Document deleted successfully!');
      setDeleteDialogOpen(false);
      setDeleteDocId(null);
      if (selectedDoc?.id === deleteDocId) {
        setSelectedDoc(null);
      }
    } catch (err) {
      setDocError('Failed to delete document. Please try again.');
      console.error('Error deleting document:', err);
    }
  };

  const handleOpenAbsenceModal = (absenceType) => {
    setSelectedAbsenceType(absenceType);
    setShowAbsenceModal(true);
  };

  const handleCloseAbsenceModal = () => {
    setShowAbsenceModal(false);
    setSelectedAbsenceType(null);
  };

  const initiateDeleteNote = (noteId) => {
    setNoteToDelete(noteId);
  };

  // 2. Triggered when clicking "Delete" in the modal
  const confirmDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      await deleteEmployeeNote(noteToDelete);

      // Optimistic update
      setNotes(prev => prev.filter(n => n.id !== noteToDelete));

      setSuccess('Feedback record deleted successfully.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Failed to delete note:", err);
      setError('Failed to delete the feedback record.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setNoteToDelete(null); // Close modal
    }
  };
  const fetchNotes = async () => {
    try {
      const notesData = await getEmployeeNotes(id);
      // Sort by incidentDate descending (Newest first)
      // Fallback to createdAt for legacy notes
      const sortedNotes = Array.isArray(notesData)
        ? notesData.sort((a, b) => new Date(b.incidentDate || b.createdAt) - new Date(a.incidentDate || a.createdAt))
        : [];
      setNotes(sortedNotes);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
      setError('Failed to load notes.');
    }
  };
  // --- NEW HANDLERS FOR FEEDBACK FORM ---
  const handleFeedbackChange = (e) => {
    const { name, value } = e.target;
    setFeedbackForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'violationType' && value !== 'Work Performance' && value !== 'Behavior' && value !== 'New Arrivals Feedback') {
        updated.noteType = 'NEGATIVE';
      }
      return updated;
    });
  };

  const handleAddNote = async () => {
    // Validate required fields
    if (!feedbackForm.description.trim() || !feedbackForm.managerName.trim()) {
      setError("Please fill in the Description and Manager Name.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      // ✅ UPDATED: Send structured object matching your API payload
      // Do NOT JSON.stringify this. The Axios service will handle it.
      const notePayload = {
        incidentDate: feedbackForm.date,
        violationType: feedbackForm.violationType,
        managerReview: feedbackForm.managerName,
        description: feedbackForm.description,
        employeeReaction: feedbackForm.employeeReaction,
        employeeAgreement: feedbackForm.employeeAgreement,
        noteType: feedbackForm.noteType
      };

      await addEmployeeNote(id, notePayload);

      // Reset Form
      setFeedbackForm({
        date: new Date().toISOString().split('T')[0],
        violationType: VIOLATION_TYPES[0],
        description: '',
        managerName: '',
        employeeReaction: '',
        employeeAgreement: '',
        noteType: 'NEGATIVE'
      });

      await fetchNotes(); // Re-fetch to see the new note in the list
      setSuccess('Feedback report added successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to add note:', err);
      setError('Failed to add note.');
    }
  };

  // Helper to parse note content safely
  const parseNoteContent = (content) => {
    try {
      const parsed = JSON.parse(content);
      if (parsed && parsed.isStructured) return parsed;
      return { isStructured: false, content: content };
    } catch (e) {
      return { isStructured: false, content: content };
    }
  };

  const handleEditClick = (note) => {
    setEditingNote(note);
    setEditForm({
      date: note.incidentDate || note.date || new Date().toISOString().split('T')[0],
      violationType: note.violationType || VIOLATION_TYPES[0],
      description: note.description || '',
      managerName: note.managerReview || '',
      employeeReaction: note.employeeReaction || '',
      employeeAgreement: note.employeeAgreement || '',
      noteType: note.noteType || 'NEGATIVE',
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'violationType' && value !== 'Work Performance' && value !== 'Behavior' && value !== 'New Arrivals Feedback') {
        updated.noteType = 'NEGATIVE';
      }
      return updated;
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.description.trim() || !editForm.managerName.trim()) {
      setError('Please fill in the Description and Manager Name.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    try {
      await updateEmployeeNote(editingNote.id, {
        incidentDate: editForm.date,
        violationType: editForm.violationType,
        managerReview: editForm.managerName,
        description: editForm.description,
        employeeReaction: editForm.employeeReaction,
        employeeAgreement: editForm.employeeAgreement,
        noteType: editForm.noteType,
      });
      setEditingNote(null);
      setEditForm({});
      await fetchNotes();
      setSuccess('Feedback updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to update note:', err);
      setError('Failed to update feedback.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleAssessmentSubmit = async () => {
    if (!selectedAbsenceForAssessment) return;

    if (!assessmentForm.violationDescription.trim()) {
      alert("Violation description is required.");
      return;
    }

    try {
      const absenceId = selectedAbsenceForAssessment.id;
      const existingViolation = absenceViolationsMap[absenceId];

      const payload = {
        absence: { id: absenceId },
        violationDescription: assessmentForm.violationDescription,
        managerReview: assessmentForm.managerReview,
        employeeReaction: assessmentForm.employeeReaction,
        employeeAgreement: assessmentForm.employeeAgreement
      };

      if (existingViolation && existingViolation.id) {
        // Update existing violation
        await updateAbsenceViolation(existingViolation.id, payload);
        setSuccess('Assessment updated successfully!');
      } else {
        // Create new violation
        await createAbsenceViolation(payload);
        setSuccess('Assessment created successfully!');
      }

      setShowAssessmentModal(false);
      setSelectedAbsenceForAssessment(null);
      setAssessmentForm({ violationDescription: '', managerReview: '', employeeReaction: '', employeeAgreement: '' });

      // Refresh violations map after save
      await loadViolationsForAbsences(absenceHistory);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to save assessment:', err);
      setError('Failed to save assessment.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return '#10B981';
      case 'pending':
        return '#F59E0B';
      case 'rejected':
        return '#6B7280';
      case 'cancelled':
        return '#6B7280';
      default:
        return '#6B7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <CheckCircle size={16} className="status-icon approved" />;
      case 'rejected':
        return <XCircle size={16} className="status-icon rejected" />;
      case 'cancelled':
        return <X size={16} className="status-icon cancelled" />;
      default:
        return <Clock size={16} className="status-icon pending" />;
    }
  };

  const handleRequestAction = async (requestId, action) => {
    if (!requestId) {
      console.error('Error: requestId is undefined or invalid');
      setError('Invalid request ID. Please try again or contact support.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    setIsProcessingRequest(true);
    try {
      let updatedRequest;
      if (action === 'APPROVED') {
        if (!window.confirm('Are you sure you want to approve this request?')) {
          setIsProcessingRequest(false);
          return;
        }
        updatedRequest = await approveRequest(requestId);
      } else if (action === 'REJECTED') {
        const reason = window.prompt('Please provide a reason for rejection:');
        if (!reason || !reason.trim()) {
          setIsProcessingRequest(false);
          return;
        }
        updatedRequest = await rejectRequest(requestId, { reason: reason.trim() });
      }
      const updatedRequests = employee.requests.map((req) =>
        req.id === requestId ? { ...req, status: action } : req
      );
      setEmployee({ ...employee, requests: updatedRequests });
      if (
        action === 'APPROVED' &&
        employee.requests.find((req) => req.id === requestId)?.type === 'ANNUAL_LEAVE'
      ) {
        try {
          const balances = await getAnnualLeaveBalanceByYear(employee.id);
          const updatedBalance = balances?.[selectedYear] ?? 0;
          setVacationDaysLeft(updatedBalance);
        } catch (err) {
          console.warn('Failed to refresh leave balance after approval:', err);
          setVacationDaysLeft((prev) => Math.max(0, prev - 1));
        }
      }
      setSuccess(`Request ${action.toLowerCase()} successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(`Error ${action.toLowerCase()} request:`, err);
      const errorMessage = err?.message || `Failed to ${action.toLowerCase()} request`;
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsProcessingRequest(false);
    }
  };

  const getRequestTotals = () => {
    if (!employee?.requests) return { byType: {}, byStatus: {} };
    const totalsByType = employee.requests.reduce((acc, req) => {
      acc[req.type] = (acc[req.type] || 0) + 1;
      return acc;
    }, {});
    const totalsByStatus = employee.requests.reduce((acc, req) => {
      acc[req.status] = (acc[req.status] || 0) + 1;
      return acc;
    }, {});
    return { byType: totalsByType, byStatus: totalsByStatus };
  };

  if (isLoading) {
    return (
      <div className="axo-page-loader">
        <div className="axo-page-loader__ring" />
        <p className="axo-page-loader__text">Loading employee profile...</p>
      </div>
    );
  }

  if (error && !employee) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <AlertCircle size={48} color="#EF4444" />
        <p>{error ? String(error) : 'An unknown error occurred'}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#3B82F6',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!employee) return null;

  const { byType, byStatus } = getRequestTotals();
  const documentCounts = documents.reduce((acc, doc) => {
    acc[doc.type] = (acc[doc.type] || 0) + 1;
    return acc;
  }, {});

  const recentDocuments = [...documents]
    .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate))
    .slice(0, 10);

  const filteredDocuments = documents.filter((doc) => {
    const matchesType = selectedDocumentType === 'All' ? true : doc.type === selectedDocumentType;
    const matchesSearch =
      docSearchTerm === '' || (doc.description?.toLowerCase().includes(docSearchTerm.toLowerCase()));
    return matchesType && matchesSearch;
  });

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'transparent',
        padding: '10px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        // overflowY: 'auto',
        // maxHeight: '100vh',
      }}
    >
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .fade-in {
          animation: slideIn 0.3s ease-out;
        }
        @media (max-width: 768px) {
          .profile-header-content {
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          }
          .profile-info-grid {
            grid-template-columns: 1fr;
          }
          .rate-cards-container {
            flex-direction: column;
            align-items: center;
            gap: 0.5rem;
          }
          .rate-card {
            min-width: 100%;
            padding: 0.5rem;
          }
          .profile-image {
            width: 80px !important;
            height: 80px !important;
          }
          .profile-name {
            font-size: 1.5rem !important;
          }
          .profile-position {
            font-size: 1rem !important;
          }
          .tabs-container {
            overflow-x: auto;
            white-space: nowrap;
            -webkit-overflow-scrolling: touch;
            flex-wrap: nowrap;
          }
          .tab-button {
            padding: 0.5rem 1rem !important;
          }
          .attendance-grid {
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          }
          .attendance-card {
            padding: 1rem !important;
          }
          .attendance-icon-container {
            width: 36px !important;
            height: 36px !important;
          }
          .time-overview-grid {
            grid-template-columns: 1fr !important;
          }
          .request-table-container {
            overflow-x: auto;
          }
          .request-table {
            min-width: 600px;
          }
          .request-table th,
          .request-table td {
            padding: 0.5rem !important;
          }
          .document-grid {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          }
          .document-card {
            padding: 1rem !important;
          }
          .contract-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          .profile-info-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .time-overview-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (min-width: 1024px) {
          .profile-info-grid {
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          }
        }
      `}</style>
      {/* Notifications */}
      {error && (
        <div
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            backgroundColor: isDark ? '#3f1415' : '#FEE2E2',
            color: isDark ? '#fca5a5' : '#991B1B',
            padding: '1rem',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxWidth: '400px',
          }}
        >
          <AlertCircle size={20} />
          <span>{String(error)}</span>
          <button
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginLeft: 'auto',
            }}
          >
            <X size={20} />
          </button>
        </div>
      )}
      {success && (
        <div
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            backgroundColor: isDark ? '#14412a' : '#D1FAE5',
            color: isDark ? '#34d399' : '#065F46',
            padding: '1rem',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxWidth: '400px',
          }}
        >
          <CheckCircle size={20} />
          <span>{String(success)}</span>
          <button
            onClick={() => setSuccess(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginLeft: 'auto',
            }}
          >
            <X size={20} />
          </button>
        </div>
      )}
      {/* Sticky Profile Header */}
      <div
        style={{
          position: 'sticky',
          top: '120px',
          zIndex: 999,
          paddingTop: 0,
          marginBottom: '2rem',
        }}
      >
        <div
          style={{
            backgroundColor: dm.card,
            borderRadius: '1rem',
            padding: '2rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2rem' }} className="profile-header-content">
            {/* Profile Picture */}
            <div style={{ position: 'relative' }}>
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt={`${employee.name || 'Employee'}'s profile`}
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '4px solid #3B82F6',
                  }}
                  className="profile-image"
                  onError={() => setProfileImageUrl(null)}
                />
              ) : (
                <div
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    border: '4px solid #2563EB',
                  }}
                  className="profile-image"
                >
                  {employee.firstName?.charAt(0) || ''}{employee.lastName?.charAt(0) || ''}
                </div>
              )}
              <div
                style={{
                  position: 'absolute',
                  bottom: '5px',
                  right: '5px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: employee.status?.toLowerCase() === 'active' ? '#10B981' : '#6B7280',
                  border: `3px solid ${dm.card}`,
                }}
              ></div>
            </div>
            {/* Employee Info + Rate Cards + Year Selector */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div>
                  <h1
                    style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      marginBottom: '0.25rem',
                      color: dm.textPrimary,
                    }}
                    className="profile-name"
                  >
                    {employee.name || 'N/A'}
                  </h1>
                  <p
                    style={{
                      fontSize: '1.25rem',
                      color: dm.textSecondary,
                      marginBottom: '1rem',
                    }}
                    className="profile-position"
                  >
                    {formatPositionsDisplay(employee)}
                  </p>
                </div>

                {/* Year Selector & Rate Cards Container */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1rem' }} className="rate-cards-wrapper">
                  {/* ✅ Year Buttons (Modified) */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: dm.surface,
                      padding: '4px',
                      borderRadius: '0.75rem',
                      gap: '8px',
                      boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    {/* Previous Year Button */}
                    <button
                      onClick={() => setSelectedYear((prev) => {
                        const currentYear = new Date().getFullYear();
                        const newYear = prev - 1;
                        return Math.max(2025, Math.min(newYear, currentYear));
                      })}
                      disabled={selectedYear <= 2025}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        border: `1px solid ${dm.border}`,
                        backgroundColor: selectedYear <= 2025 ? dm.surface : dm.yearBtnBg,
                        color: selectedYear <= 2025 ? dm.textMuted : dm.yearBtnColor,
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: selectedYear <= 2025 ? 'not-allowed' : 'pointer',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        transition: 'all 0.2s',
                        opacity: selectedYear <= 2025 ? 0.5 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (selectedYear > 2025) {
                          e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#F9FAFB';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedYear > 2025) {
                          e.currentTarget.style.backgroundColor = dm.yearBtnBg;
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      <ChevronLeft size={16} />
                      Previous Year
                    </button>

                    {/* Display Selected Year */}
                    <span
                      style={{
                        padding: '0 0.5rem',
                        fontSize: '1rem',
                        fontWeight: '700',
                        color: dm.textPrimary,
                        minWidth: '60px',
                        textAlign: 'center',
                      }}
                    >
                      {selectedYear}
                    </span>

                    {/* Next Year Button - NEW */}
                    <button
                      onClick={() => setSelectedYear((prev) => {
                        const currentYear = new Date().getFullYear();
                        const newYear = prev + 1;
                        return Math.max(2025, Math.min(newYear, currentYear));
                      })}
                      disabled={selectedYear >= new Date().getFullYear()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        border: `1px solid ${dm.border}`,
                        backgroundColor: selectedYear >= new Date().getFullYear() ? dm.surface : dm.yearBtnBg,
                        color: selectedYear >= new Date().getFullYear() ? dm.textMuted : dm.yearBtnColor,
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: selectedYear >= new Date().getFullYear() ? 'not-allowed' : 'pointer',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        transition: 'all 0.2s',
                        opacity: selectedYear >= new Date().getFullYear() ? 0.5 : 1,
                      }}
                      onMouseEnter={(e) => {
                        const currentYear = new Date().getFullYear();
                        if (selectedYear < currentYear) {
                          e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#F9FAFB';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        const currentYear = new Date().getFullYear();
                        if (selectedYear < currentYear) {
                          e.currentTarget.style.backgroundColor = dm.yearBtnBg;
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      Next Year
                      <ChevronLeft size={16} style={{ transform: 'rotate(180deg)' }} />
                    </button>

                    {/* Current Year Button */}
                    <button
                      onClick={() => setSelectedYear(new Date().getFullYear())}
                      disabled={selectedYear === new Date().getFullYear()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        border:
                          selectedYear === new Date().getFullYear()
                            ? '1px solid transparent'
                            : '1px solid #E5E7EB',
                        backgroundColor:
                          selectedYear === new Date().getFullYear()
                            ? '#3B82F6'
                            : dm.yearBtnBg,
                        color:
                          selectedYear === new Date().getFullYear()
                            ? 'white'
                            : dm.yearBtnColor,
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor:
                          selectedYear === new Date().getFullYear()
                            ? 'default'
                            : 'pointer',
                        boxShadow:
                          selectedYear === new Date().getFullYear()
                            ? 'none'
                            : '0 1px 2px rgba(0,0,0,0.05)',
                        opacity:
                          selectedYear === new Date().getFullYear() ? 0.9 : 1,
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedYear !== new Date().getFullYear()) {
                          e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#F9FAFB';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedYear !== new Date().getFullYear()) {
                          e.currentTarget.style.backgroundColor = dm.yearBtnBg;
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      Current Year
                      {selectedYear !== new Date().getFullYear() && (
                        <RotateCcw size={14} />
                      )}
                    </button>

                  </div>
                  {/* Rate Cards */}
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'flex-end' }} className="rate-cards-container">
                    {/* Lateness >= 10m Rate */}
                    <div
                      style={{
                        backgroundColor: isDark ? (latenessOver10Rate > 2 ? '#3f1415' : '#14412a') : (latenessOver10Rate > 2 ? '#FEE2E2' : '#D1FAE5'),
                        border: '1px solid',
                        borderColor: latenessOver10Rate > 2 ? '#EF4444' : '#c2f50bff',
                        borderRadius: '0.5rem',
                        padding: '0.5rem 1rem',
                        minWidth: '120px',
                      }}
                      className="rate-card"
                    >
                      <p
                        style={{
                          fontSize: '0.75rem',
                          color: isDark ? (latenessOver10Rate > 2 ? '#fca5a5' : '#34d399') : (latenessOver10Rate > 2 ? '#991B1B' : '#065F46'),
                          margin: 0,
                        }}
                      >
                        Lateness ≥ 10m
                      </p>
                      <p
                        style={{
                          fontSize: '1.25rem',
                          fontWeight: 'bold',
                          color: isDark ? (latenessOver10Rate > 2 ? '#fca5a5' : '#34d399') : (latenessOver10Rate > 2 ? '#991B1B' : '#065F46'),
                          margin: 0,
                        }}
                      >
                        {ratesLoading ? '...' : `${latenessOver10Rate}%`}
                      </p>
                    </div>

                    {/* Lateness Rate */}
                    <div
                      style={{
                        backgroundColor: isDark ? (latenessRate > 2 ? '#3f1415' : '#14412a') : (latenessRate > 2 ? '#FEE2E2' : '#D1FAE5'),
                        border: '1px solid',
                        borderColor: latenessRate > 2 ? '#EF4444' : '#10B981',
                        borderRadius: '0.5rem',
                        padding: '0.5rem 1rem',
                        minWidth: '120px',
                      }}
                      className="rate-card"
                    >
                      <p
                        style={{
                          fontSize: '0.75rem',
                          color: isDark ? (latenessRate > 2 ? '#fca5a5' : '#34d399') : (latenessRate > 2 ? '#991B1B' : '#065F46'),
                          margin: 0,
                        }}
                      >
                        Lateness Rate
                      </p>
                      <p
                        style={{
                          fontSize: '1.25rem',
                          fontWeight: 'bold',
                          color: isDark ? (latenessRate > 2 ? '#fca5a5' : '#34d399') : (latenessRate > 2 ? '#991B1B' : '#065F46'),
                          margin: 0,
                        }}
                      >
                        {ratesLoading ? '...' : `${latenessRate}%`}
                      </p>
                    </div>

                    {/* Sickness Rate */}
                    <div
                      style={{
                        backgroundColor: isDark ? (sicknessRate > 2 ? '#3f1415' : '#14412a') : (sicknessRate > 2 ? '#FEE2E2' : '#D1FAE5'),
                        border: '1px solid',
                        borderColor: sicknessRate > 2 ? '#EF4444' : '#10B981',
                        borderRadius: '0.5rem',
                        padding: '0.5rem 1rem',
                        minWidth: '120px',
                      }}
                      className="rate-card"
                    >
                      <p
                        style={{
                          fontSize: '0.75rem',
                          color: isDark ? (sicknessRate > 2 ? '#fca5a5' : '#34d399') : (sicknessRate > 2 ? '#991B1B' : '#065F46'),
                          margin: 0,
                        }}
                      >
                        Sickness Rate
                      </p>
                      <p
                        style={{
                          fontSize: '1.25rem',
                          fontWeight: 'bold',
                          color: isDark ? (sicknessRate > 2 ? '#fca5a5' : '#34d399') : (sicknessRate > 2 ? '#991B1B' : '#065F46'),
                          margin: 0,
                        }}
                      >
                        {ratesLoading ? '...' : `${sicknessRate}%`}
                      </p>
                    </div>

                    {/* Personal Rate */}
                    <div
                      style={{
                        backgroundColor: isDark ? (personalRate > 2 ? '#3f1415' : '#14412a') : (personalRate > 2 ? '#FEE2E2' : '#D1FAE5'),
                        border: '1px solid',
                        borderColor: personalRate > 2 ? '#EF4444' : '#10B981',
                        borderRadius: '0.5rem',
                        padding: '0.5rem 1rem',
                        minWidth: '120px',
                      }}
                      className="rate-card"
                    >
                      <p
                        style={{
                          fontSize: '0.75rem',
                          color: isDark ? (personalRate > 2 ? '#fca5a5' : '#34d399') : (personalRate > 2 ? '#991B1B' : '#065F46'),
                          margin: 0,
                        }}
                      >
                        Personal Rate
                      </p>
                      <p
                        style={{
                          fontSize: '1.25rem',
                          fontWeight: 'bold',
                          color: isDark ? (personalRate > 2 ? '#fca5a5' : '#34d399') : (personalRate > 2 ? '#991B1B' : '#065F46'),
                          margin: 0,
                        }}
                      >
                        {ratesLoading ? '...' : `${personalRate}%`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem',
                  marginTop: '1.5rem',
                }}
                className="profile-info-grid"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Mail size={18} color="#3B82F6" />
                  <div>
                    <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem' }}>
                      Email
                    </p>
                    <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary }}>
                      {employee.user?.email || 'N/A'}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Phone size={18} color="#3B82F6" />
                  <div>
                    <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem' }}>
                      Phone
                    </p>
                    <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary }}>
                      {employee.phone || 'N/A'}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <MapPin size={18} color="#3B82F6" />
                  <div>
                    <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem' }}>
                      Office
                    </p>
                    <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary }}>
                      {employee.office || 'N/A'}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Briefcase size={18} color="#3B82F6" />
                  <div>
                    <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem' }}>Company</p>
                    <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary }}>{employee.company || 'N/A'}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar size={18} color="#3B82F6" />
                  <div>
                    <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem' }}>
                      Join Date
                    </p>
                    <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary }}>
                      {formatDate(employee.joinDate)}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CreditCard size={18} color="#3B82F6" /> {/* Or choose another suitable icon */}
                  <div>
                    <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem' }}>
                      CNSS
                    </p>
                    <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary }}>
                      {employee.cnss || 'N/A'}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CreditCard size={18} color="#3B82F6" />
                  <div>
                    <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem' }}>
                      CIN
                    </p>
                    <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary }}>
                      {employee.cin || 'N/A'}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <User size={18} color="#3B82F6" />
                  <div>
                    <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem' }}>
                      Gender
                    </p>
                    <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary }}>
                      {employee.gender || 'N/A'}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar size={18} color="#3B82F6" />
                  <div>
                    <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem' }}>
                      Date of Birth
                    </p>
                    <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary }}>
                      {formatDate(employee.dob)}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Clock size={18} color="#3B82F6" />
                  <div>
                    <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem' }}>
                      Age
                    </p>
                    <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary }}>
                      {calculateAge(employee.dob) !== 'N/A' ? `${calculateAge(employee.dob)} years old` : 'N/A'}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Award size={18} color="#3B82F6" />
                  <div>
                    <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem' }}>
                      Diploma
                    </p>
                    <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary }}>
                      {employee.diploma || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div
        style={{
          backgroundColor: 'var(--card-bg, white)',
          borderRadius: '1rem',
          padding: '0.5rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
        }}
        className="tabs-container"
      >
        <button
          onClick={() => setActiveTab('attendance')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '0.5rem',
            backgroundColor: activeTab === 'attendance' ? '#3B82F6' : dm.btnInactiveBg,
            color: activeTab === 'attendance' ? 'white' : dm.btnInactiveColor,
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
          }}
          className="tab-button"
        >
          <Clock size={18} />
          Attendance Tracking
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '0.5rem',
            backgroundColor: activeTab === 'requests' ? '#3B82F6' : dm.btnInactiveBg,
            color: activeTab === 'requests' ? 'white' : dm.btnInactiveColor,
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
            position: 'relative',
          }}
          className="tab-button"
        >
          <FileText size={18} />
          Requests
          {employee.requests?.filter((r) => r.status?.toLowerCase() === 'pending').length > 0 && (
            <span
              style={{
                backgroundColor: '#EF4444',
                color: 'white',
                borderRadius: '9999px',
                padding: '0.125rem 0.5rem',
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              {employee.requests.filter((r) => r.status?.toLowerCase() === 'pending').length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '0.5rem',
            backgroundColor: activeTab === 'documents' ? '#3B82F6' : dm.btnInactiveBg,
            color: activeTab === 'documents' ? 'white' : dm.btnInactiveColor,
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
          }}
          className="tab-button"
        >
          <FileText size={18} />
          Documents
          {documents.length > 0 && (
            <span
              style={{
                backgroundColor: activeTab === 'documents' ? 'rgba(255,255,255,0.3)' : (isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB'),
                color: activeTab === 'documents' ? 'white' : dm.textSecondary,
                borderRadius: '9999px',
                padding: '0.125rem 0.5rem',
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              {documents.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('contract')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '0.5rem',
            backgroundColor: activeTab === 'contract' ? '#3B82F6' : dm.btnInactiveBg,
            color: activeTab === 'contract' ? 'white' : dm.btnInactiveColor,
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
          }}
          className="tab-button"
        >
          <Briefcase size={18} />
          Contract Details
        </button>
        <button
          onClick={() => setActiveTab('notes')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '0.5rem',
            backgroundColor: activeTab === 'notes' ? '#3B82F6' : dm.btnInactiveBg,
            color: activeTab === 'notes' ? 'white' : dm.btnInactiveColor,
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
            position: 'relative',
          }}
          className="tab-button"
        >
          <FileText size={18} />
          Feedback
          {notes.length > 0 && (
            <span style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: '#EF4444',
              color: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 'bold',
            }}>
              {notes.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('careers')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '0.5rem',
            backgroundColor: activeTab === 'careers' ? '#3B82F6' : dm.btnInactiveBg,
            color: activeTab === 'careers' ? 'white' : dm.btnInactiveColor,
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
          }}
          className="tab-button"
        >
          <TrendingUp size={18} />
          Careers
          {careers.length > 0 && (
            <span style={{
              backgroundColor: activeTab === 'careers' ? 'rgba(255,255,255,0.3)' : (isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB'),
              color: activeTab === 'careers' ? 'white' : dm.textSecondary,
              borderRadius: '9999px',
              padding: '0.125rem 0.5rem',
              fontSize: '0.75rem',
              fontWeight: 'bold',
            }}>{careers.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('contract-feedback')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '0.5rem',
            backgroundColor: activeTab === 'contract-feedback' ? '#3B82F6' : dm.btnInactiveBg,
            color: activeTab === 'contract-feedback' ? 'white' : dm.btnInactiveColor,
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
          }}
          className="tab-button"
        >
          <MessageSquare size={18} />
          Contract Feedback
        </button>
        {/* <button
          onClick={() => setActiveTab('bonuses')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '0.5rem',
            backgroundColor: activeTab === 'bonuses' ? '#3B82F6' : dm.btnInactiveBg,
            color: activeTab === 'bonuses' ? 'white' : dm.btnInactiveColor,
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
          }}
          className="tab-button"
        >
          <CreditCard size={18} />
          Bonuses
        </button> */}
        <button
          onClick={() => setActiveTab('overtime')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '0.5rem',
            backgroundColor: activeTab === 'overtime' ? '#3B82F6' : dm.btnInactiveBg,
            color: activeTab === 'overtime' ? 'white' : dm.btnInactiveColor,
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
          }}
          className="tab-button"
        >
          <Timer size={18} />
          Overtime
        </button>
      </div>
      {/* Tab Content */}
      <div className="fade-in">
        {activeTab === 'attendance' && (
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem',
              }}
              className="attendance-grid"
            >
              <div
                onClick={() => handleOpenAbsenceModal('ANNUAL_LEAVE')}
                style={{
                  backgroundColor: 'var(--card-bg, white)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  border: '2px solid transparent',
                }}
                className="attendance-card"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.2)';
                  e.currentTarget.style.borderColor = '#3B82F6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '0.75rem',
                      backgroundColor: isDark ? '#1e3a5f' : '#DBEAFE',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                    }}
                    className="attendance-icon-container"
                  >
                    🏖️
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '0.875rem', color: dm.textSecondary, marginBottom: '0.25rem' }}>
                      Vacation Days left
                    </h3>
                    <p style={{ fontSize: '2rem', fontWeight: 'bold', color: dm.textPrimary }}>
                      {vacationDaysLeft !== null ? vacationDaysLeft : '...'}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: dm.progressTrack,
                    borderRadius: '9999px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${vacationDaysLeft !== null ? Math.min((vacationDaysLeft / 22) * 100, 100) : 0
                        }%`,
                      height: '100%',
                      backgroundColor: '#3B82F6',
                      borderRadius: '9999px',
                      transition: 'width 0.3s',
                    }}
                  ></div>
                </div>
              </div>
              <div
                onClick={() => handleOpenAbsenceModal('SICKNESS')}
                style={{
                  backgroundColor: 'var(--card-bg, white)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  border: '2px solid transparent',
                }}
                className="attendance-card"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.2)';
                  e.currentTarget.style.borderColor = '#EF4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '0.75rem',
                      backgroundColor: isDark ? '#3f1415' : '#FEE2E2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                    }}
                    className="attendance-icon-container"
                  >
                    🤒
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '0.875rem', color: dm.textSecondary, marginBottom: '0.25rem' }}>
                      Sick Records
                    </h3>
                    <p style={{ fontSize: '2rem', fontWeight: 'bold', color: dm.textPrimary }}>
                      {sickDays || 0}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: dm.progressTrack,
                    borderRadius: '9999px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${sickDays ? Math.min((sickDays / 10) * 100, 100) : 0}%`,
                      height: '100%',
                      backgroundColor: '#EF4444',
                      borderRadius: '9999px',
                      transition: 'width 0.3s',
                    }}
                  ></div>
                </div>
              </div>
              <div
                onClick={() => handleOpenAbsenceModal('PERSONAL')}
                style={{
                  backgroundColor: 'var(--card-bg, white)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  border: '2px solid transparent',
                }}
                className="attendance-card"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(168, 85, 247, 0.2)';
                  e.currentTarget.style.borderColor = '#A855F7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '0.75rem',
                      backgroundColor: isDark ? '#2e1065' : '#F3E8FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                    }}
                    className="attendance-icon-container"
                  >
                    🎒
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '0.875rem', color: dm.textSecondary, marginBottom: '0.25rem' }}>
                      Personal Records
                    </h3>
                    <p style={{ fontSize: '2rem', fontWeight: 'bold', color: dm.textPrimary }}>
                      {personalDays || 0}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: dm.progressTrack,
                    borderRadius: '9999px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${personalDays ? Math.min((personalDays / 15) * 100, 100) : 0}%`,
                      height: '100%',
                      backgroundColor: '#A855F7',
                      borderRadius: '9999px',
                      transition: 'width 0.3s',
                    }}
                  ></div>
                </div>
              </div>
              <div
                onClick={() => handleOpenAbsenceModal('LATENESS')}
                style={{
                  backgroundColor: 'var(--card-bg, white)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  border: '2px solid transparent',
                }}
                className="attendance-card"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.2)';
                  e.currentTarget.style.borderColor = '#F59E0B';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '0.75rem',
                      backgroundColor: '#FEF3C7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                    }}
                    className="attendance-icon-container"
                  >
                    ⏰
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.25rem' }}>
                      Lateness
                    </h3>
                    <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>
                      {latenessDays || 0}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#E5E7EB',
                    borderRadius: '9999px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${latenessDays ? Math.min((latenessDays / 10) * 100, 100) : 0}%`,
                      height: '100%',
                      backgroundColor: '#F59E0B',
                      borderRadius: '9999px',
                      transition: 'width 0.3s',
                    }}
                  ></div>
                </div>
              </div>
              <div
                onClick={() => handleOpenAbsenceModal('UNPAID_LEAVE')}
                style={{
                  backgroundColor: 'var(--card-bg, white)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  border: '2px solid transparent',
                }}
                className="attendance-card"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.2)';
                  e.currentTarget.style.borderColor = '#6B7280';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '0.75rem',
                      backgroundColor: '#F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                    }}
                    className="attendance-icon-container"
                  >
                    💼
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.25rem' }}>
                      Unpaid Leave
                    </h3>
                    <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>
                      {unpaidDays || 0}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#E5E7EB',
                    borderRadius: '9999px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${unpaidDays ? Math.min((unpaidDays / 30) * 100, 100) : 0}%`,
                      height: '100%',
                      backgroundColor: '#6B7280',
                      borderRadius: '9999px',
                      transition: 'width 0.3s',
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: 'var(--card-bg, white)',
                borderRadius: '1rem',
                padding: '2rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <h2
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  marginBottom: '1.5rem',
                  color: '#111827',
                }}
              >
                Time Overview
              </h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '2rem',
                }}
                className="time-overview-grid"
              >
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem' }}>
                    Time Employed
                  </p>
                  <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3B82F6' }}>
                    {employee.timeOverview?.formattedDuration || 'N/A'}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.25rem' }}>
                    {employee.timeOverview?.workedDays || 0} total days
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem' }}>
                    Actual Days Worked
                  </p>
                  <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10B981' }}>
                    {employee.timeOverview?.actualWorkedDays || 'N/A'}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.25rem' }}>
                    Workdays only
                  </p>
                </div>
              </div>
            </div>
            <AbsenceRecordsModal
              open={showAbsenceModal}
              onClose={handleCloseAbsenceModal}
              employeeId={employee.id}
              absenceType={selectedAbsenceType}
              employeeName={employee.name || 'N/A'}
              selectedYear={selectedYear} // ✅ Pass the year
            />

          </div>
        )}
        {/* ... Rest of tabs (Requests, Documents, Contract, Notes, Evaluation) ... */}
        {activeTab === 'requests' && (
          <div
            style={{
              backgroundColor: 'var(--card-bg, white)',
              borderRadius: '1rem',
              padding: '2rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}
            >
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
                Request History
              </h2>
              <span
                style={{
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                }}
              >
                {employee.requests?.length || 0} Total
              </span>
            </div>
            {employee.requests?.length > 0 ? (
              <div style={{ overflowX: 'auto' }} className="request-table-container">
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }} className="request-table">
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                      <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Type</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Details</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Date</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Status</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employee.requests.map((req, index) => (
                      <tr key={req.id || index} style={{ borderBottom: '1px solid #F3F4F6', transition: 'background-color 0.2s' }}>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#111827' }}>{req.type || 'N/A'}</td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>{req.details || req.description || 'N/A'}</td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>{formatDate(req.date || req.submissionDate || req.createdAt)}</td>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {getStatusIcon(req.status)}
                            <span style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '500', backgroundColor: req.status?.toLowerCase() === 'approved' ? '#D1FAE5' : req.status?.toLowerCase() === 'pending' ? '#FEF3C7' : '#F3F4F6', color: req.status?.toLowerCase() === 'approved' ? '#065F46' : req.status?.toLowerCase() === 'pending' ? '#92400E' : '#374151' }}>
                              {req.status || 'Pending'}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          {req.status && req.status.toLowerCase() === 'pending' && req.id ? (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button onClick={() => handleRequestAction(req.id, 'APPROVED')} disabled={isProcessingRequest} style={{ padding: '0.5rem 1rem', backgroundColor: '#10B981', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: '500', cursor: isProcessingRequest ? 'not-allowed' : 'pointer' }}>Approve</button>
                              <button onClick={() => handleRequestAction(req.id, 'REJECTED')} disabled={isProcessingRequest} style={{ padding: '0.5rem 1rem', backgroundColor: '#6B7280', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: '500', cursor: isProcessingRequest ? 'not-allowed' : 'pointer' }}>Reject</button>
                            </div>
                          ) : (
                            <span style={{ fontSize: '0.875rem', color: '#9CA3AF', fontStyle: 'italic' }}>{req.status?.toLowerCase() === 'approved' ? 'Approved' : 'No actions available'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF' }}>
                <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <p style={{ fontSize: '1.125rem' }}>No requests available.</p>
              </div>
            )}
          </div>
        )}
        {activeTab === 'documents' && (
          <div>
            {docError && (
              <div style={{
                backgroundColor: '#FEE2E2',
                color: '#991B1B',
                padding: '1rem',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <AlertCircle size={20} />
                <span>{String(docError)}</span>
                <button onClick={() => setDocError(null)} style={{
                  marginLeft: 'auto',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer'
                }}>
                  <X size={20} />
                </button>
              </div>
            )}
            {docSuccess && (
              <div style={{
                backgroundColor: '#D1FAE5',
                color: '#065F46',
                padding: '1rem',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <CheckCircle size={20} />
                <span>{String(docSuccess)}</span>
                <button onClick={() => setDocSuccess(null)} style={{
                  marginLeft: 'auto',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer'
                }}>
                  <X size={20} />
                </button>
              </div>
            )}
            {/* Document Controls */}
            <div style={{
              backgroundColor: 'var(--card-bg, white)',
              borderRadius: '1rem',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    <FileType size={16} />
                    Document Type
                  </label>
                  <select
                    value={selectedDocumentType}
                    onChange={(e) => setSelectedDocumentType(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'var(--card-bg, white)',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="All">All Documents</option>
                    <option value="Contract">Contract</option>
                    <option value="National ID">National ID</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Payroll">Payroll</option>
                    <option value="Personal Documents">Personal Documents</option>
                    <option value="Code of Conduct">Code of Conduct</option>
                    <option value="Probation Period Agreement">Probation Period Agreement</option>
                    <option value="Bank Details Form">Bank Details Form</option>
                    <option value="Promotion Letters">Promotion Letters</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    <Search size={16} />
                    Search Documents
                  </label>
                  <input
                    type="text"
                    placeholder="Search by description..."
                    value={docSearchTerm}
                    onChange={(e) => setDocSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  Showing {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => setShowUploadForm(!showUploadForm)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
                >
                  <Upload size={16} />
                  Upload Document
                </button>
              </div>
            </div>
            {/* Upload Form */}
            {showUploadForm && (
              <div style={{
                backgroundColor: 'var(--card-bg, white)',
                borderRadius: '1rem',
                padding: '2rem',
                marginBottom: '1.5rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#111827'
                }}>
                  <Upload size={20} />
                  Upload New Document
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                      Document Type *
                    </label>
                    <select
                      name="type"
                      value={uploadForm.type}
                      onChange={handleUploadChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        backgroundColor: 'var(--card-bg, white)'
                      }}
                    >
                      <option value="Contract">Contract</option>
                      <option value="National ID">National ID</option>
                      <option value="Diploma">Diploma</option>
                      <option value="Payroll">Payroll</option>
                      <option value="Personal Documents">Personal Documents</option>
                      <option value="Code of Conduct">Code of Conduct</option>
                      <option value="Probation Period Agreement">Probation Period Agreement</option>
                      <option value="Bank Details Form">Bank Details Form</option>
                      <option value="Promotion Letters">Promotion Letters</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                      Description *
                    </label>
                    <input
                      type="text"
                      name="description"
                      value={uploadForm.description}
                      onChange={handleUploadChange}
                      placeholder="Enter document description"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                      File *
                    </label>
                    <input
                      type="file"
                      name="file"
                      onChange={handleUploadChange}
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={handleUploadSubmit}
                    disabled={uploading}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#10B981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      opacity: uploading ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {uploading ? (
                      <>
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FileText size={16} />
                        Upload Document
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowUploadForm(false);
                      setUploadForm({ type: 'Contract', description: '', file: null });
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#6B7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {/* Documents List — View via Modal */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem'
            }} className="document-grid">
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    style={{
                      backgroundColor: 'var(--card-bg, white)',
                      borderRadius: '1rem',
                      padding: '1.5rem',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      border: '2px solid transparent',
                      cursor: 'pointer'
                    }}
                    className="document-card"
                    onClick={() => setSelectedDoc(doc)} // ✅ Open modal on click
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      e.currentTarget.style.borderColor = '#3B82F6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', flex: 1 }}>
                        {doc.description}
                      </h3>
                      <span style={{
                        backgroundColor: '#DBEAFE',
                        color: '#1E40AF',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        marginLeft: '0.5rem'
                      }}>
                        {doc.type}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                      <Calendar size={14} color="#6B7280" />
                      <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                        {formatDate(doc.uploadDate)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click
                          setDeleteDocId(doc.id);
                          setDeleteDialogOpen(true);
                        }}
                        style={{
                          color: '#6B7280',
                          padding: '0.5rem',
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '3rem',
                  backgroundColor: 'var(--card-bg, white)',
                  borderRadius: '1rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.5, color: '#9CA3AF' }} />
                  <p style={{ fontSize: '1.125rem', color: '#6B7280' }}>
                    No {selectedDocumentType === 'All' ? '' : selectedDocumentType.toLowerCase()} documents found.
                  </p>
                </div>
              )}
            </div>
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogContent>Are you sure you want to delete this document?</DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleDeleteDocument} style={{ color: '#6B7280' }}>
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
            {/* ✅ Use DocumentDetailsModal for viewing */}
            {selectedDoc && (
              <DocumentDetailsModal
                selectedDocument={selectedDoc}
                setSelectedDocument={setSelectedDoc}
                fetchDocuments={fetchEmployeeDocuments}
                isEditMode={true} // ViewProfile is read-only for viewing
                setIsEditMode={() => { }} // No-op
              />
            )}
          </div>
        )}

        {/* ========== CONTRACT DETAILS TAB ========== */}
        {activeTab === 'contract' && (
          <div
            style={{
              backgroundColor: dm.card,
              borderRadius: '1rem',
              padding: '2rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: `1px solid ${dm.border}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
                borderBottom: `1px solid ${dm.border}`,
                paddingBottom: '1rem',
              }}
            >
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: dm.textPrimary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  margin: 0,
                }}
              >
                <Briefcase size={22} className="text-blue-500" />
                Contract Details
              </h2>
              {contract && (
                <span
                  style={{
                    backgroundColor:
                      contract.status?.toLowerCase() === 'active'
                        ? '#D1FAE5'
                        : '#F3F4F6',
                    color:
                      contract.status?.toLowerCase() === 'active'
                        ? '#065F46'
                        : '#374151',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                  }}
                >
                  {contract.status || 'N/A'}
                </span>
              )}
            </div>

            {contract ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '1.5rem',
                }}
              >
                <div>
                  <p style={{ fontSize: '0.8rem', color: dm.textSecondary, marginBottom: '0.35rem', textTransform: 'uppercase', fontWeight: '600' }}>
                    Contract Type
                  </p>
                  <p style={{ fontSize: '1rem', fontWeight: '600', color: dm.textPrimary, margin: 0 }}>
                    {contract.type || 'N/A'}
                  </p>
                </div>

                <div>
                  <p style={{ fontSize: '0.8rem', color: dm.textSecondary, marginBottom: '0.35rem', textTransform: 'uppercase', fontWeight: '600' }}>
                    Start Date
                  </p>
                  <p style={{ fontSize: '1rem', fontWeight: '600', color: dm.textPrimary, margin: 0 }}>
                    {contract.startDate ? formatDate(contract.startDate) : 'N/A'}
                  </p>
                </div>

                <div>
                  <p style={{ fontSize: '0.8rem', color: dm.textSecondary, marginBottom: '0.35rem', textTransform: 'uppercase', fontWeight: '600' }}>
                    End Date
                  </p>
                  <p style={{ fontSize: '1rem', fontWeight: '600', color: dm.textPrimary, margin: 0 }}>
                    {contract.endDate ? formatDate(contract.endDate) : 'Ongoing / Indefinite'}
                  </p>
                </div>

                <div>
                  <p style={{ fontSize: '0.8rem', color: dm.textSecondary, marginBottom: '0.35rem', textTransform: 'uppercase', fontWeight: '600' }}>
                    Status
                  </p>
                  <p
                    style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color:
                        contract.status?.toLowerCase() === 'active'
                          ? '#10B981'
                          : dm.textSecondary,
                      margin: 0,
                    }}
                  >
                    {contract.status || 'N/A'}
                  </p>
                </div>

                <div>
                  <p style={{ fontSize: '0.8rem', color: dm.textSecondary, marginBottom: '0.35rem', textTransform: 'uppercase', fontWeight: '600' }}>
                    Work Hours
                  </p>
                  <p style={{ fontSize: '1rem', fontWeight: '600', color: dm.textPrimary, margin: 0 }}>
                    {contract.workHours != null ? `${contract.workHours} hours/week` : 'N/A'}
                  </p>
                </div>

                <div>
                  <p style={{ fontSize: '0.8rem', color: dm.textSecondary, marginBottom: '0.35rem', textTransform: 'uppercase', fontWeight: '600' }}>
                    Salary
                  </p>
                  <p style={{ fontSize: '1rem', fontWeight: '600', color: dm.textPrimary, margin: 0 }}>
                    {contract.salary != null ? `${contract.salary.toLocaleString()} MAD` : 'N/A'}
                  </p>
                </div>

                {contract.file && (
                  <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                    <p style={{ fontSize: '0.8rem', color: dm.textSecondary, marginBottom: '0.35rem', textTransform: 'uppercase', fontWeight: '600' }}>
                      Contract Document
                    </p>
                    <a
                      href={`${API_URL}/contracts/contracts/${encodeURIComponent(contract.file)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: '#EFF6FF',
                        color: '#2563EB',
                        border: '1px solid #BFDBFE',
                        borderRadius: '0.5rem',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                      }}
                    >
                      <FileText size={16} />
                      View Contract File
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: dm.textSecondary }}>
                <Briefcase size={36} style={{ margin: '0 auto 0.75rem', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '0.95rem' }}>No contract found for this employee.</p>
              </div>
            )}
          </div>
        )}

        {/* --- REPLACED FEEDBACK SECTION --- */}
        {activeTab === 'notes' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

            {/* --- NEW FEEDBACK FORM --- */}
            <div style={{
              backgroundColor: 'var(--card-bg, white)',
              borderRadius: '1rem',
              padding: '2rem',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem', color: '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FileText size={24} className="text-blue-500" />
                New Feedback Record
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>

                {/* Date Selector (Fixed Click Issue) */}
                {/* Date Selector (MUI DatePicker) */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Incident Date
                  </label>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={new Date(feedbackForm.date)}
                      onChange={(newValue) => {
                        if (newValue) {
                          setFeedbackForm(prev => ({
                            ...prev,
                            date: format(newValue, 'yyyy-MM-dd') // Store as YYYY-MM-DD for API
                          }));
                        }
                      }}
                      format="dd-MM-yyyy" // Display as DD-MM-YYYY
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: {
                            backgroundColor: 'var(--card-bg, white)',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '0.5rem',
                              '& fieldset': { borderColor: '#D1D5DB' }, // Match existing border color
                              '&:hover fieldset': { borderColor: '#9CA3AF' },
                              '&.Mui-focused fieldset': { borderColor: '#3B82F6', borderWidth: '1px' } // Match existing focus color
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '0.875rem',
                              padding: '0.76rem', // Match padding of adjacent inputs
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </div>
                {/* Violation Type Dropdown */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Type of Violation
                  </label>
                  <div style={{ position: 'relative' }}>
                    <AlertTriangle size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#EF4444' }} />
                    <select
                      name="violationType"
                      value={feedbackForm.violationType}
                      onChange={handleFeedbackChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        backgroundColor: 'var(--card-bg, white)',
                        appearance: 'none'
                      }}
                    >
                      {VIOLATION_TYPES.map(type => {
                        const counts = violationTypeCounts[type] || { negative: 0, positive: 0 };
                        const negCount = counts.negative;
                        const posCount = counts.positive;

                        let labelText = type;
                        if (negCount > 0) labelText += ` (${negCount})`;
                        if (posCount >= 3) labelText += ` Positive (${posCount})`;

                        let textColor = 'inherit';
                        let fw = 'normal';
                        if (negCount >= 3) {
                          textColor = '#EF4444';
                          fw = '600';
                        } else if (posCount >= 3) {
                          textColor = '#166534';
                          fw = '600';
                        }

                        return (
                          <option
                            key={type}
                            value={type}
                            style={{ color: textColor, fontWeight: fw }}
                          >
                            {labelText}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                {/* Note Type Selector (Conditional) */}
                {(feedbackForm.violationType === 'Work Performance' || feedbackForm.violationType === 'Behavior' || feedbackForm.violationType === 'New Arrivals Feedback') && (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                      Feedback Type
                    </label>
                    <select
                      name="noteType"
                      value={feedbackForm.noteType}
                      onChange={handleFeedbackChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `1px solid ${feedbackForm.noteType === 'NEGATIVE' ? '#FCA5A5' : '#86EFAC'}`,
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        backgroundColor: feedbackForm.noteType === 'NEGATIVE' ? '#FEF2F2' : '#F0FDF4',
                        color: feedbackForm.noteType === 'NEGATIVE' ? '#991B1B' : '#166534',
                      }}
                    >
                      <option value="NEGATIVE" style={{ color: '#991B1B', backgroundColor: 'white' }}>Negative</option>
                      <option value="POSITIVE" style={{ color: '#166534', backgroundColor: 'white' }}>Positive</option>
                    </select>
                  </div>
                )}

                {/* Manager Review */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Manager Review by
                  </label>
                  <div style={{ position: 'relative' }}>
                    <UserCheck size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#3B82F6' }} />
                    <input
                      type="text"
                      name="managerName"
                      placeholder="Enter manager feedback"
                      value={feedbackForm.managerName}
                      onChange={handleFeedbackChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  Description of Incident
                </label>
                <RichTextEditor
                  value={feedbackForm.description || ''}
                  onChange={(val) => handleFeedbackChange({ target: { name: 'description', value: val } })}
                />
              </div>

              {/* Employee Reaction and Employee Agreement / Action - Side by Side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                {/* Employee Reaction */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Employee Reaction
                  </label>
                  <div style={{ position: 'relative' }}>
                    <RichTextEditor
                      value={feedbackForm.employeeReaction || ''}
                      onChange={(val) => handleFeedbackChange({ target: { name: 'employeeReaction', value: val } })}
                    />
                  </div>
                </div>

                {/* Employee Agreement / Action */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Employee Agreement / Action
                  </label>
                  <div style={{ position: 'relative' }}>
                    <RichTextEditor
                      value={feedbackForm.employeeAgreement || ''}
                      onChange={(val) => handleFeedbackChange({ target: { name: 'employeeAgreement', value: val } })}
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleAddNote}
                  style={{
                    padding: '0.75rem 2rem',
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: '600',
                    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
                >
                  <Send size={18} />
                  Submit Feedback
                </button>
              </div>
            </div>

            {/* --- FEEDBACK HISTORY LIST --- */}
            {/* --- UPDATED FEEDBACK HISTORY LIST --- */}
            {/* --- UPDATED FEEDBACK HISTORY SECTION WITH TABS & FILTERS --- */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#374151', margin: 0 }}>
                  History
                </h3>

                {/* FILTERS */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <select
                    value={feedbackYear}
                    onChange={(e) => setFeedbackYear(parseInt(e.target.value))}
                    style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #D1D5DB', fontSize: '0.875rem' }}
                  >
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>

                  <select
                    value={feedbackMonth}
                    onChange={(e) => setFeedbackMonth(e.target.value)}
                    style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #D1D5DB', fontSize: '0.875rem' }}
                  >
                    <option value="All">All Months</option>
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                      <option key={i} value={i}>{m}</option>
                    ))}
                  </select>

                  <button
                    onClick={() => setFeedbackSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #D1D5DB',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      color: '#4B5563',
                      fontWeight: '500'
                    }}
                  >
                    {feedbackSortOrder === 'desc' ? <ArrowDownNarrowWide size={16} /> : <ArrowUpNarrowWide size={16} />}
                    {feedbackSortOrder === 'desc' ? 'Newest' : 'Oldest'}
                  </button>
                </div>
              </div>

              {/* TABS */}
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '1px solid #E5E7EB' }}>
                <button
                  onClick={() => setFeedbackTab('violations')}
                  style={{
                    padding: '0.75rem 1rem',
                    borderBottom: feedbackTab === 'violations' ? '2px solid #3B82F6' : '2px solid transparent',
                    color: feedbackTab === 'violations' ? '#3B82F6' : '#6B7280',
                    fontWeight: '600',
                    background: 'none',
                    border: 'none',
                    borderBottomWidth: '2px',
                    borderBottomStyle: 'solid',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontSize: '0.9rem'
                  }}
                >
                  Violations
                </button>
                <button
                  onClick={() => setFeedbackTab('attendance')}
                  style={{
                    padding: '0.75rem 1rem',
                    borderBottom: feedbackTab === 'attendance' ? '2px solid #3B82F6' : '2px solid transparent',
                    color: feedbackTab === 'attendance' ? '#3B82F6' : '#6B7280',
                    fontWeight: '600',
                    background: 'none',
                    border: 'none',
                    borderBottomWidth: '2px',
                    borderBottomStyle: 'solid',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontSize: '0.9rem'
                  }}
                >
                  Attendance Records
                </button>
              </div>

              {/* CONTENT: VIOLATIONS TAB */}
              {feedbackTab === 'violations' && (
                <div>
                  {(() => {
                    // Filter Notes
                    const filteredNotes = notes
                      .filter(note => {
                        const noteDate = new Date(note.incidentDate || note.createdAt);
                        const matchYear = noteDate.getFullYear() === feedbackYear;
                        const matchMonth = feedbackMonth === 'All' || noteDate.getMonth() === parseInt(feedbackMonth);
                        return matchYear && matchMonth;
                      })
                      .sort((a, b) => {
                        const dateA = new Date(a.incidentDate || a.createdAt);
                        const dateB = new Date(b.incidentDate || b.createdAt);
                        return feedbackSortOrder === 'desc' ? dateB - dateA : dateA - dateB;
                      });

                    if (filteredNotes.length === 0) {
                      return (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF', backgroundColor: 'var(--card-bg, white)', borderRadius: '1rem' }}>
                          <p>No violation records found for this period.</p>
                        </div>
                      );
                    }

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {filteredNotes.map(note => {
                          const isNewStructure = !!note.incidentDate;
                          return (
                            <div key={note.id} style={{ backgroundColor: 'var(--card-bg, white)', borderRadius: '0.75rem', overflow: 'hidden', border: `1px solid ${editingNote?.id === note.id ? '#3B82F6' : '#E5E7EB'}`, boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'border-color 0.2s' }}>
                              {isNewStructure ? (
                                // RENDER NEW STRUCTURE
                                <div>
                                  {editingNote?.id === note.id ? (
                                    // ── EDIT MODE ──
                                    <div style={{ padding: '1.5rem' }}>
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                                        <div>
                                          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Date</label>
                                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                              value={editForm.date ? new Date(editForm.date) : null}
                                              onChange={(val) => { if (val) setEditForm((prev) => ({ ...prev, date: format(val, 'yyyy-MM-dd') })); }}
                                              slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                          </LocalizationProvider>
                                        </div>
                                        <div>
                                          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Violation Type</label>
                                          <select
                                            name="violationType"
                                            value={editForm.violationType}
                                            onChange={handleEditFormChange}
                                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #D1D5DB', borderRadius: '0.375rem', fontSize: '0.875rem', backgroundColor: 'white', color: '#111827' }}
                                          >
                                            {VIOLATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                          </select>
                                        </div>
                                        {(editForm.violationType === 'Work Performance' || editForm.violationType === 'Behavior' || editForm.violationType === 'New Arrivals Feedback') && (
                                          <div>
                                            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Feedback Type</label>
                                            <select
                                              name="noteType"
                                              value={editForm.noteType}
                                              onChange={handleEditFormChange}
                                              style={{
                                                width: '100%',
                                                padding: '0.5rem',
                                                border: `1px solid ${editForm.noteType === 'NEGATIVE' ? '#FCA5A5' : '#86EFAC'}`,
                                                borderRadius: '0.375rem',
                                                fontSize: '0.875rem',
                                                backgroundColor: editForm.noteType === 'NEGATIVE' ? '#FEF2F2' : '#F0FDF4',
                                                color: editForm.noteType === 'NEGATIVE' ? '#991B1B' : '#166534'
                                              }}
                                            >
                                              <option value="NEGATIVE" style={{ color: '#991B1B', backgroundColor: 'white' }}>Negative</option>
                                              <option value="POSITIVE" style={{ color: '#166534', backgroundColor: 'white' }}>Positive</option>
                                            </select>
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ marginBottom: '0.75rem' }}>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Manager Name</label>
                                        <input
                                          name="managerName"
                                          value={editForm.managerName}
                                          onChange={handleEditFormChange}
                                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #D1D5DB', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#111827', boxSizing: 'border-box' }}
                                        />
                                      </div>
                                      <div style={{ marginBottom: '0.75rem' }}>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Description</label>
                                        <RichTextEditor
                                          value={editForm.description || ''}
                                          onChange={(val) => handleEditFormChange({ target: { name: 'description', value: val } })}
                                        />
                                      </div>
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                        <div>
                                          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#8B5CF6', display: 'block', marginBottom: '0.25rem' }}>Employee Reaction</label>
                                          <RichTextEditor
                                            value={editForm.employeeReaction || ''}
                                            onChange={(val) => handleEditFormChange({ target: { name: 'employeeReaction', value: val } })}
                                          />
                                        </div>
                                        <div>
                                          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#F59E0B', display: 'block', marginBottom: '0.25rem' }}>Agreement / Action</label>
                                          <RichTextEditor
                                            value={editForm.employeeAgreement || ''}
                                            onChange={(val) => handleEditFormChange({ target: { name: 'employeeAgreement', value: val } })}
                                          />
                                        </div>
                                      </div>
                                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                                        <button
                                          onClick={() => { setEditingNote(null); setEditForm({}); }}
                                          style={{ padding: '0.5rem 1.25rem', backgroundColor: 'transparent', color: '#6B7280', border: '1px solid #D1D5DB', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          onClick={handleSaveEdit}
                                          style={{ padding: '0.5rem 1.25rem', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
                                        >
                                          Save Changes
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    // ── READ-ONLY MODE ──
                                    <>
                                      <div style={{ padding: '1rem 1.5rem', backgroundColor: '#F8FAFC', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                          <span style={{
                                            backgroundColor: note.noteType === 'POSITIVE' ? '#DCFCE7' : '#FEE2E2',
                                            color: note.noteType === 'POSITIVE' ? '#166534' : '#991B1B',
                                            padding: '0.25rem 0.75rem',
                                            borderRadius: '9999px',
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            border: `1px solid ${note.noteType === 'POSITIVE' ? '#BBF7D0' : '#FECACA'}`
                                          }}>
                                            {note.violationType}
                                          </span>
                                          <span style={{ fontSize: '0.875rem', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                            <CalendarDays size={14} />
                                            {formatDate(note.incidentDate)}
                                          </span>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                          <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>Created: {formatDate(note.createdDate)} {note.createdBy && <span style={{ color: '#9CA3AF' }}>by {note.createdBy}</span>}</span>
                                          {note.lastModifiedBy && (
                                            <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                                              Modified: {note.lastModifiedDate ? formatDate(note.lastModifiedDate) + ' ' : ''}by {note.lastModifiedBy}
                                            </span>
                                          )}
                                          <button
                                            onClick={() => handleEditClick(note)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3B82F6', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', transition: 'background-color 0.2s' }}
                                            title="Edit Record"
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#EFF6FF'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                          >
                                            <Edit2 size={16} />
                                          </button>
                                          <button
                                            onClick={() => initiateDeleteNote(note.id)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', transition: 'background-color 0.2s' }}
                                            title="Delete Record"
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                          >
                                            <Trash2 size={16} />
                                          </button>
                                        </div>
                                      </div>

                                      <div style={{ padding: '1.5rem' }}>
                                        <div style={{ marginBottom: '1rem' }}>
                                          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Manager Review</h4>
                                          <p style={{ fontSize: '0.9rem', fontWeight: '500', color: '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <UserCheck size={16} color="#3B82F6" />
                                            {note.managerReview}
                                          </p>
                                        </div>
                                        <div style={{ marginBottom: '1rem' }}>
                                          <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '600', color: '#6B7280', marginBottom: '0.25rem' }}>Description</h4>
                                          <div style={{ fontSize: '0.9rem', color: '#1F2937', lineHeight: '1.5', backgroundColor: '#F9FAFB', padding: '1rem', borderRadius: '0.5rem' }}
                                            dangerouslySetInnerHTML={{ __html: note.description || '' }}
                                          />
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', backgroundColor: '#F9FAFB', padding: '1rem', borderRadius: '0.5rem' }}>
                                          <div>
                                            <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '600', color: '#6B7280', marginBottom: '0.25rem' }}>Employee Reaction</h4>
                                            <div style={{ fontSize: '0.9rem', color: '#4B5563', fontStyle: 'italic', lineHeight: '1.5' }}
                                              dangerouslySetInnerHTML={{ __html: '"' + (note.employeeReaction || 'No comment provided') + '"' }}
                                            />
                                          </div>
                                          <div>
                                            <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '600', color: '#6B7280', marginBottom: '0.25rem' }}>Employee Agreement / Action</h4>
                                            <div style={{ fontSize: '0.9rem', color: '#4B5563', lineHeight: '1.5' }}
                                              dangerouslySetInnerHTML={{ __html: note.employeeAgreement || 'No agreement provided' }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                // RENDER LEGACY TEXT NOTE
                                <div style={{ padding: '1.5rem' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <p style={{ fontSize: '0.95rem', color: '#1F2937', marginBottom: '0.5rem', flex: 1 }}>{note.content}</p>
                                    <button
                                      onClick={() => initiateDeleteNote(note.id)}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', transition: 'background-color 0.2s' }}
                                      title="Delete Record"
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                  <p style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                                    {formatDate(note.createdAt)} by {note.createdBy || 'Unknown'}
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* CONTENT: ATTENDANCE TAB */}
              {feedbackTab === 'attendance' && (
                <div>
                  {violationsLoading && (
                    <div style={{ textAlign: 'center', padding: '1rem', color: '#6B7280', fontSize: '0.875rem' }}>
                      Loading violation data...
                    </div>
                  )}
                  {(() => {
                    const relevantTypes = ['SICKNESS', 'LATENESS', 'PERSONAL', 'PERSONAL_COMMITMENT'];
                    const filteredAbsences = absenceHistory
                      .filter(record => {
                        const type = record.ty_pe?.toUpperCase();
                        if (!relevantTypes.includes(type)) return false;
                        const date = new Date(record.startDate || record.date || record.createdAt);
                        const matchMonth = feedbackMonth === 'All' || date.getMonth() === parseInt(feedbackMonth);
                        return matchMonth;
                      })
                      .sort((a, b) => {
                        const dateA = new Date(a.startDate || a.date || a.createdAt);
                        const dateB = new Date(b.startDate || b.date || b.createdAt);
                        return feedbackSortOrder === 'desc' ? dateB - dateA : dateA - dateB;
                      });

                    if (filteredAbsences.length === 0) {
                      return (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF', backgroundColor: 'var(--card-bg, white)', borderRadius: '1rem' }}>
                          <p>No attendance records (Sick, Late, Personal) found for this period.</p>
                        </div>
                      );
                    }

                    return (
                      <div style={{ backgroundColor: 'var(--card-bg, white)', borderRadius: '0.75rem', overflowX: 'auto', border: '1px solid #E5E7EB', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                        <table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '2px solid #E5E7EB' }}>
                              <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '100px' }}>Date</th>
                              <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '100px' }}>Type</th>
                              <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '100px' }}>Duration</th>
                              <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '150px' }}>Violation Description</th>
                              <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '150px' }}>Manager Review</th>
                              <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '200px' }}>Employee Reaction</th>
                              <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '200px' }}>Employee Agreement</th>
                              <th style={{ padding: '0.875rem 1rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '100px' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredAbsences.map((record, idx) => {
                              const type = record.ty_pe?.toUpperCase();
                              let typeColor = '#6B7280';
                              let typeBg = '#F3F4F6';
                              if (type === 'SICKNESS') { typeColor = '#EF4444'; typeBg = '#FEE2E2'; }
                              else if (type === 'LATENESS') { typeColor = '#F59E0B'; typeBg = '#FEF3C7'; }
                              else if (type && type.includes('PERSONAL')) { typeColor = '#8B5CF6'; typeBg = '#EDE9FE'; }

                              const violation = absenceViolationsMap[record.id];
                              const hasViolation = !!violation;

                              return (
                                <tr
                                  key={record.id || idx}
                                  style={{
                                    borderBottom: '1px solid #F3F4F6',
                                    backgroundColor: idx % 2 === 0 ? 'white' : '#FAFAFA',
                                    transition: 'background-color 0.15s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F0F7FF'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? 'white' : '#FAFAFA'}
                                >
                                  {/* Date */}
                                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#111827', whiteSpace: 'nowrap' }}>
                                    {formatDate(record.startDate || record.date)}
                                  </td>

                                  {/* Type Badge */}
                                  <td style={{ padding: '1rem' }}>
                                    <span style={{
                                      backgroundColor: typeBg,
                                      color: typeColor,
                                      padding: '0.25rem 0.625rem',
                                      borderRadius: '9999px',
                                      fontSize: '0.75rem',
                                      fontWeight: '700',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {record.ty_pe}
                                    </span>
                                  </td>

                                  {/* Duration */}
                                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#4B5563' }}>
                                    {(() => {
                                      const type = record.ty_pe?.toUpperCase();
                                      if (type === 'LATENESS') {
                                        const mins = record.hours ? Math.round(record.hours * 60) : (record.durationInMinutes || 0);
                                        if (mins > 0) return `${mins} min${mins !== 1 ? 's' : ''}`;
                                        return record.days ? `${record.days} day${record.days !== 1 ? 's' : ''}` : '—';
                                      }
                                      if (type === 'PERSONAL' || type === 'PERSONAL_COMMITMENT') {
                                        const hrs = record.hours || (record.days ? record.days * 8 : 0);
                                        if (hrs > 0) return `${hrs} hour${hrs !== 1 ? 's' : ''}`;
                                        return record.days ? `${record.days} day${record.days !== 1 ? 's' : ''}` : '—';
                                      }
                                      return record.days ? `${record.days} day${record.days !== 1 ? 's' : ''}` : '—';
                                    })()}
                                  </td>

                                  {/* Violation Description */}
                                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: hasViolation ? '#1F2937' : '#9CA3AF', maxWidth: '180px' }}>
                                    {hasViolation && violation.violationDescription
                                      ? <span title={stripHtml(violation.violationDescription)} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} dangerouslySetInnerHTML={{ __html: violation.violationDescription }} />
                                      : <span style={{ fontStyle: 'italic', color: '#D1D5DB' }}>—</span>
                                    }
                                  </td>

                                  {/* Manager Review */}
                                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: hasViolation ? '#1F2937' : '#9CA3AF', maxWidth: '180px' }}>
                                    {hasViolation && violation.managerReview
                                      ? <span title={stripHtml(violation.managerReview)} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} dangerouslySetInnerHTML={{ __html: violation.managerReview }} />
                                      : <span style={{ fontStyle: 'italic', color: '#D1D5DB' }}>—</span>
                                    }
                                  </td>

                                  {/* Employee Reaction */}
                                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: hasViolation ? '#1F2937' : '#9CA3AF', maxWidth: '180px' }}>
                                    {hasViolation && violation.employeeReaction
                                      ? <span title={stripHtml(violation.employeeReaction)} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} dangerouslySetInnerHTML={{ __html: violation.employeeReaction }} />
                                      : <span style={{ fontStyle: 'italic', color: '#D1D5DB' }}>—</span>
                                    }
                                  </td>

                                  {/* Employee Agreement */}
                                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: hasViolation ? '#1F2937' : '#9CA3AF', maxWidth: '180px' }}>
                                    {hasViolation && violation.employeeAgreement
                                      ? <span title={stripHtml(violation.employeeAgreement)} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} dangerouslySetInnerHTML={{ __html: violation.employeeAgreement }} />
                                      : <span style={{ fontStyle: 'italic', color: '#D1D5DB' }}>—</span>
                                    }
                                  </td>

                                  {/* Assess Button */}
                                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                                    <button
                                      onClick={() => {
                                        setSelectedAbsenceForAssessment(record);
                                        setAssessmentForm({
                                          violationDescription: violation?.violationDescription || '',
                                          managerReview: violation?.managerReview || '',
                                          employeeReaction: violation?.employeeReaction || '',
                                          employeeAgreement: violation?.employeeAgreement || ''
                                        });
                                        setShowAssessmentModal(true);
                                      }}
                                      style={{
                                        padding: '0.375rem 0.875rem',
                                        backgroundColor: hasViolation ? '#EFF6FF' : '#F0FDF4',
                                        color: hasViolation ? '#2563EB' : '#16A34A',
                                        border: `1px solid ${hasViolation ? '#BFDBFE' : '#BBF7D0'}`,
                                        borderRadius: '0.5rem',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: '600',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.375rem',
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.15s'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = hasViolation ? '#DBEAFE' : '#DCFCE7';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = hasViolation ? '#EFF6FF' : '#F0FDF4';
                                        e.currentTarget.style.boxShadow = 'none';
                                      }}
                                      title={hasViolation ? 'Edit assessment' : 'Add assessment'}
                                    >
                                      <IconEyeEdit size={14} />
                                      {hasViolation ? 'Edit' : 'Assess'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              )}

            </div>
            {/* --- DELETE CONFIRMATION DIALOG --- */}
            <Dialog
              open={!!noteToDelete}
              onClose={() => setNoteToDelete(null)}
              PaperProps={{
                sx: { borderRadius: '16px', padding: '12px', maxWidth: '400px', width: '100%' }
              }}
            >
              <Box sx={{ textAlign: 'center', pt: 1 }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  backgroundColor: '#FEE2E2', color: '#DC2626',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'
                }}>
                  <Trash2 size={24} />
                </div>

                <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: '#111827' }}>
                  Delete Feedback Record?
                </Typography>

                <Typography variant="body2" sx={{ color: '#6B7280', px: 2, mb: 3, lineHeight: 1.5 }}>
                  Are you sure you want to delete this record permanently? This action cannot be undone.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setNoteToDelete(null)}
                    sx={{
                      borderRadius: '8px',
                      color: '#374151',
                      borderColor: '#D1D5DB',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={confirmDeleteNote}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#DC2626',
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: 'none',
                      '&:hover': { backgroundColor: '#B91C1C', boxShadow: 'none' }
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Dialog>
          </div>
        )}

        {/* ========== CAREERS TAB ========== */}
        {activeTab === 'careers' && employee && (() => {
          // Lazy load career records
          if (!careersLoaded) {
            getEmployeeCareers(employee.id)
              .then(data => { setCareers(data || []); setCareersLoaded(true); })
              .catch(() => setCareersLoaded(true));
          }
          // Lazy load dropdown data (departments only — projects & positions use constants)
          if (!careerDropdownsLoaded) {
            setCareerDropdownsLoaded(true);
            api.get(`${API_URL}/departments`)
              .then(res => setAllDepartments(res.data || []))
              .catch(() => { });
          }

          const handleCareerFormChange = (e) => {
            const { name, value } = e.target;
            setCareerForm(prev => ({ ...prev, [name]: value }));
          };

          const handleCareerSubmit = async () => {
            if (!careerForm.positionTitle.trim() || !careerForm.startDate) {
              return;
            }
            try {
              if (editingCareer) {
                const updated = await updateEmployeeCareer(employee.id, editingCareer.id, careerForm);
                setCareers(prev => prev.map(c => c.id === editingCareer.id ? updated : c));
              } else {
                const created = await createEmployeeCareer(employee.id, careerForm);
                setCareers(prev => [created, ...prev]);
              }
              setCareerForm(emptyCareerForm);
              setEditingCareer(null);
              setShowCareerForm(false);
            } catch (err) {
              console.error('Failed to save career:', err);
            }
          };

          const handleCareerEdit = (career) => {
            setEditingCareer(career);
            // Sync selected projects
            const projList = career.projectNames
              ? career.projectNames.split(',').map(p => p.trim()).filter(Boolean)
              : [];
            setSelectedProjectIds(projList);
            setCareerForm({
              positionTitle: career.positionTitle || '',
              department: career.department || '',
              startDate: career.startDate || '',
              endDate: career.endDate || '',
              employmentType: career.employmentType || 'Full-time',
              description: career.description || '',
              projectNames: career.projectNames || '',
            });
            setShowCareerForm(true);
          };

          const handleCareerDelete = async (careerId) => {
            if (!window.confirm('Delete this career record?')) return;
            try {
              await deleteEmployeeCareer(employee.id, careerId);
              setCareers(prev => prev.filter(c => c.id !== careerId));
            } catch (err) {
              console.error('Failed to delete career:', err);
            }
          };

          const inputStyle = {
            width: '100%', padding: '0.5rem 0.75rem',
            border: `1px solid ${dm.border}`,
            borderRadius: '0.5rem', fontSize: '0.875rem',
            backgroundColor: dm.card, color: dm.textPrimary,
            boxSizing: 'border-box',
          };

          const labelStyle = {
            fontSize: '0.75rem', fontWeight: '600',
            color: dm.textSecondary, display: 'block', marginBottom: '0.25rem',
          };

          const typeColors = {
            'Full-time': { bg: '#DBEAFE', text: '#1D4ED8' },
            'Part-time': { bg: '#FEF3C7', text: '#92400E' },
            'Internship': { bg: '#D1FAE5', text: '#065F46' },
            'Contract': { bg: '#EDE9FE', text: '#5B21B6' },
            'Freelance': { bg: '#FCE7F3', text: '#9D174D' },
            'Consultant': { bg: '#E0F2FE', text: '#0369A1' },
          };

          return (
            <div style={{ backgroundColor: dm.card, borderRadius: '1rem', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: dm.textPrimary, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <TrendingUp size={22} color="#3B82F6" /> Career History
                  </h2>
                  <p style={{ fontSize: '0.875rem', color: dm.textSecondary, margin: '0.25rem 0 0' }}>
                    Position history, projects, and career milestones
                  </p>
                </div>
                <button
                  onClick={() => { setEditingCareer(null); setCareerForm(emptyCareerForm); setShowCareerForm(prev => !prev); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.6rem 1.25rem', backgroundColor: showCareerForm && !editingCareer ? '#6B7280' : '#3B82F6',
                    color: 'white', border: 'none', borderRadius: '0.6rem',
                    fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  <Plus size={16} />
                  {showCareerForm && !editingCareer ? 'Cancel' : 'Add Entry'}
                </button>
              </div>

              {/* Add / Edit Form */}
              {showCareerForm && (
                <div style={{
                  backgroundColor: isDark ? '#1e293b' : '#F8FAFF',
                  border: `1px solid ${editingCareer ? '#8B5CF6' : '#3B82F6'}`,
                  borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '2rem',
                }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', color: editingCareer ? '#7C3AED' : '#2563EB', marginBottom: '1.25rem', marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {editingCareer ? <Edit2 size={16} /> : <Plus size={16} />}
                    {editingCareer ? 'Edit Career Record' : 'New Career Entry'}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                    {/* Position Title - dropdown from CAREER_POSITION_OPTIONS + custom */}
                    <div>
                      <label style={labelStyle}>Position Title *</label>
                      <select
                        name="positionTitle"
                        value={CAREER_POSITION_OPTIONS.includes(careerForm.positionTitle) ? careerForm.positionTitle : '__custom__'}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') {
                            setCareerForm(prev => ({ ...prev, positionTitle: '' }));
                          } else {
                            setCareerForm(prev => ({ ...prev, positionTitle: e.target.value }));
                          }
                        }}
                        style={inputStyle}
                      >
                        <option value="">-- Select position --</option>
                        {CAREER_POSITION_OPTIONS.map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                        <option value="__custom__">✏️ Custom title...</option>
                      </select>
                      {(!CAREER_POSITION_OPTIONS.includes(careerForm.positionTitle) || careerForm.positionTitle === '') && (
                        <input
                          name="positionTitle"
                          value={careerForm.positionTitle}
                          onChange={handleCareerFormChange}
                          placeholder="Type custom position title"
                          style={{ ...inputStyle, marginTop: '0.4rem' }}
                        />
                      )}
                    </div>
                    {/* Department - dropdown from /api/departments */}
                    <div>
                      <label style={labelStyle}>Department</label>
                      <select
                        name="department"
                        value={careerForm.department}
                        onChange={handleCareerFormChange}
                        style={inputStyle}
                      >
                        <option value="">-- Select department --</option>
                        {allDepartments.map(d => (
                          <option key={d.departmentId || d.id} value={d.name || d.departmentName}>
                            {d.name || d.departmentName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Employment Type</label>
                      <select name="employmentType" value={careerForm.employmentType} onChange={handleCareerFormChange} style={inputStyle}>
                        {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Start Date *</label>
                      <input type="date" name="startDate" value={careerForm.startDate} onChange={handleCareerFormChange} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>End Date <span style={{ fontWeight: 400, color: dm.textMuted }}>(leave blank if current)</span></label>
                      <input type="date" name="endDate" value={careerForm.endDate} onChange={handleCareerFormChange} style={inputStyle} />
                    </div>
                    <div style={{ gridColumn: 'span 1' }}>
                      <label style={labelStyle}>Projects <span style={{ fontWeight: 400, color: dm.textMuted }}>(select all that apply)</span></label>
                      <div style={{
                        border: `1px solid ${dm.border}`, borderRadius: '0.5rem',
                        backgroundColor: dm.card, maxHeight: '140px', overflowY: 'auto', padding: '0.5rem'
                      }}>
                        {CAREER_PROJECT_OPTIONS.map(name => {
                          const checked = (careerForm.projectNames || '').split(',').map(s => s.trim()).filter(Boolean).includes(name);
                          return (
                            <label key={name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.2rem 0.3rem', cursor: 'pointer', fontSize: '0.8rem', color: dm.textPrimary }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const current = (careerForm.projectNames || '').split(',').map(s => s.trim()).filter(Boolean);
                                  const updated = e.target.checked
                                    ? [...current.filter(n => n !== name), name]
                                    : current.filter(n => n !== name);
                                  setCareerForm(prev => ({ ...prev, projectNames: updated.join(', ') }));
                                }}
                                style={{ accentColor: '#3B82F6' }}
                              />
                              {name}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={labelStyle}>Description / Responsibilities</label>
                    <RichTextEditor
                      value={careerForm.description || ''}
                      onChange={(val) => handleCareerFormChange({ target: { name: 'description', value: val } })}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                    <button onClick={() => { setShowCareerForm(false); setEditingCareer(null); setCareerForm(emptyCareerForm); }}
                      style={{ padding: '0.5rem 1.25rem', background: 'transparent', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', cursor: 'pointer', color: dm.textSecondary, fontWeight: 600, fontSize: '0.875rem' }}>
                      Cancel
                    </button>
                    <button onClick={handleCareerSubmit}
                      disabled={!careerForm.positionTitle.trim() || !careerForm.startDate}
                      style={{ padding: '0.5rem 1.25rem', backgroundColor: editingCareer ? '#7C3AED' : '#3B82F6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', opacity: (!careerForm.positionTitle.trim() || !careerForm.startDate) ? 0.6 : 1 }}>
                      {editingCareer ? 'Save Changes' : 'Add Entry'}
                    </button>
                  </div>
                </div>
              )}

              {/* Timeline */}
              {!careersLoaded ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: dm.textSecondary }}>
                  <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '0.5rem' }} />
                  <p>Loading career history...</p>
                </div>
              ) : careers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: dm.textSecondary }}>
                  <TrendingUp size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                  <p style={{ fontSize: '1rem', fontWeight: '600', color: dm.textPrimary, marginBottom: '0.5rem' }}>No career records yet</p>
                  <p style={{ fontSize: '0.875rem' }}>Add career entries to build the employee's career timeline.</p>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  {/* Vertical line */}
                  <div style={{ position: 'absolute', left: '18px', top: '8px', bottom: '8px', width: '2px', backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB', borderRadius: '2px' }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {careers.map((career, idx) => {
                      const typeColor = typeColors[career.employmentType] || { bg: '#F3F4F6', text: '#374151' };
                      const isCurrent = !career.endDate;
                      const projects = career.projectNames ? career.projectNames.split(',').map(p => p.trim()).filter(Boolean) : [];
                      return (
                        <div key={career.id} style={{ display: 'flex', gap: '1rem', paddingLeft: '0' }}>
                          {/* Dot */}
                          <div style={{ flexShrink: 0, width: '38px', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '4px' }}>
                            <div style={{
                              width: '14px', height: '14px', borderRadius: '50%', marginTop: '6px',
                              backgroundColor: isCurrent ? '#3B82F6' : (isDark ? '#475569' : '#CBD5E1'),
                              border: `3px solid ${isCurrent ? '#BFDBFE' : (isDark ? '#1e293b' : 'white')}`,
                              boxShadow: isCurrent ? '0 0 0 3px rgba(59,130,246,0.2)' : 'none',
                              zIndex: 1, position: 'relative',
                            }} />
                          </div>
                          {/* Card */}
                          <div style={{
                            flex: 1, backgroundColor: dm.card, border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB'}`,
                            borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s',
                          }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'}
                          >
                            {/* Card Header */}
                            <div style={{ padding: '1rem 1.25rem', backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#F0F0F0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                                  <h3 style={{ fontSize: '1rem', fontWeight: '700', color: dm.textPrimary, margin: 0 }}>{career.positionTitle}</h3>
                                  {isCurrent && (
                                    <span style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8', fontSize: '0.65rem', fontWeight: '700', padding: '0.2rem 0.5rem', borderRadius: '9999px', letterSpacing: '0.04em' }}>CURRENT</span>
                                  )}
                                  <span style={{ backgroundColor: typeColor.bg, color: typeColor.text, fontSize: '0.7rem', fontWeight: '600', padding: '0.2rem 0.6rem', borderRadius: '9999px' }}>{career.employmentType || 'Full-time'}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                  {career.department && (
                                    <span style={{ fontSize: '0.8rem', color: dm.textSecondary, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                      <Building2 size={13} /> {career.department}
                                    </span>
                                  )}
                                  <span style={{ fontSize: '0.8rem', color: dm.textSecondary, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <CalendarDays size={13} />
                                    {formatDate(career.startDate)} — {isCurrent ? <span style={{ color: '#10B981', fontWeight: '600' }}>Present</span> : formatDate(career.endDate)}
                                  </span>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button onClick={() => handleCareerEdit(career)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3B82F6', padding: '4px 6px', borderRadius: '6px', display: 'flex', alignItems: 'center', transition: 'background 0.2s' }}
                                  onMouseEnter={e => e.currentTarget.style.backgroundColor = isDark ? 'rgba(59,130,246,0.15)' : '#EFF6FF'}
                                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                  title="Edit">
                                  <Edit2 size={15} />
                                </button>
                                <button onClick={() => handleCareerDelete(career.id)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '4px 6px', borderRadius: '6px', display: 'flex', alignItems: 'center', transition: 'background 0.2s' }}
                                  onMouseEnter={e => e.currentTarget.style.backgroundColor = isDark ? 'rgba(239,68,68,0.15)' : '#FEE2E2'}
                                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                  title="Delete">
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </div>
                            {/* Card Body */}
                            {(career.description || projects.length > 0) && (
                              <div style={{ padding: '1rem 1.25rem' }}>
                                {career.description && (
                                  <div style={{ fontSize: '0.875rem', color: dm.textSecondary, lineHeight: '1.6', marginBottom: projects.length > 0 ? '0.75rem' : 0, marginTop: 0 }}
                                    dangerouslySetInnerHTML={{ __html: career.description }}
                                  />
                                )}
                                {projects.length > 0 && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <FolderOpen size={14} color="#6B7280" />
                                    {projects.map((proj, i) => (
                                      <span key={i} style={{ backgroundColor: isDark ? 'rgba(99,102,241,0.15)' : '#EEF2FF', color: isDark ? '#a5b4fc' : '#4338CA', fontSize: '0.75rem', fontWeight: '500', padding: '0.2rem 0.6rem', borderRadius: '9999px', border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#C7D2FE'}` }}>
                                        {proj}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        {activeTab === 'contract-feedback' && employee && (() => {
          const formatDateText = (dateStr) => {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '';
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}-${month}-${year}`;
          };

          if (!contractFeedbacksLoaded) {
            setContractFeedbacksLoaded(true);
            getAllContractRenewalFeedbacks()
              .then(data => {
                const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim().toLowerCase();
                const filtered = (data || []).filter(fb => fb.employeeName?.trim().toLowerCase() === fullName);
                setContractFeedbacks(filtered);
              })
              .catch(err => console.error(err));

            getContractsByEmployeeId(employee.id)
              .then(contracts => {
                setEmpContracts(contracts || []);
              })
              .catch(err => console.error(err));
          }

          const getFeedbackId = (fb) => fb.id || fb.feedbackId || fb.contractRenewalFeedbackId;

          const getContractTypeDefaults = (type, employeeObj) => {
            const firstName = employeeObj?.firstName || '';
            const lastName = employeeObj?.lastName || '';
            const fullName = `${firstName} ${lastName}`.replace(/\s+/g, ' ').trim().toLowerCase();

            // Special employee override
            if (fullName === 'oumaima attaya' || fullName === 'rania ramchi') {
              return { maxExtensions: 0, finalPossible: 'CDI' };
            }

            const normType = String(type || '').toUpperCase().trim();
            switch (normType) {
              case 'CDD':
                return { maxExtensions: 2, finalPossible: 'CDI' };
              case 'CDI':
                return { maxExtensions: 1, finalPossible: 'CDI' };
              case 'PCS':
                return { maxExtensions: 0, finalPossible: 'CDI' };
              case 'ANAPEC':
                return { maxExtensions: 1, finalPossible: 'CDI' };
              case 'INTERNSHIP':
                return { maxExtensions: 1, finalPossible: 'CDI' };
              default:
                return { maxExtensions: 0, finalPossible: 'CDI' };
            }
          };

          const handleContractChange = (contractId) => {
            if (!contractId) {
              setContractRenewalForm(prev => ({
                ...prev,
                contractId: '',
                numberOfExtensions: '',
                finalExtensionPossible: ''
              }));
              return;
            }

            const selectedContract = empContracts.find(c => String(c.contractId || c.id) === String(contractId));
            const contractType = selectedContract ? selectedContract.type : '';
            const defaults = getContractTypeDefaults(contractType, employee);

            setContractRenewalForm(prev => ({
              ...prev,
              contractId,
              numberOfExtensions: defaults.maxExtensions,
              finalExtensionPossible: defaults.finalPossible
            }));
          };

          const handleSubmitFeedback = async (e) => {
            e.preventDefault();
            if (!contractRenewalForm.contractId) {
              alert('Please select a contract first.');
              return;
            }
            setIsSavingContractFeedback(true);
            try {
              const body = {
                employeeId: employee.id,
                contractId: contractRenewalForm.contractId,
                managerFeedback: contractRenewalForm.managerFeedback || null,
                projectManagerFeedback: contractRenewalForm.projectManagerFeedback || null,
                hrFeedback: contractRenewalForm.hrFeedback || null,
                actionRequired: contractRenewalForm.actionRequired || null,
                numberOfExtensions: contractRenewalForm.numberOfExtensions !== '' ? Number(contractRenewalForm.numberOfExtensions) : null,
                finalExtensionPossible: contractRenewalForm.finalExtensionPossible || null
              };

              const created = await createContractRenewalFeedback(body);
              setContractFeedbacks(prev => [created, ...prev]);
              alert('Feedback submitted successfully!');

              setContractRenewalForm({
                contractId: '',
                managerFeedback: '',
                projectManagerFeedback: '',
                hrFeedback: '',
                actionRequired: '',
                numberOfExtensions: '',
                finalExtensionPossible: ''
              });
            } catch (err) {
              console.error('Failed to submit feedback:', err);
              const errMsg = err.response?.data?.message || err.response?.data || err.message || '';
              if (typeof errMsg === 'string' && errMsg.toLowerCase().includes('already has renewal feedback')) {
                alert('This contract already has renewal feedback. Please edit the existing feedback below instead of creating a new one.');
              } else {
                alert(`Failed to submit feedback: ${typeof errMsg === 'string' && errMsg ? errMsg : 'Make sure you have authorized permissions.'}`);
              }
            } finally {
              setIsSavingContractFeedback(false);
            }
          };

          const handleUpdateFeedback = async () => {
            if (!editingContractFeedbackId) return;
            setIsSavingContractFeedback(true);
            try {
              const body = {
                managerFeedback: editContractForm.managerFeedback || null,
                projectManagerFeedback: editContractForm.projectManagerFeedback || null,
                hrFeedback: editContractForm.hrFeedback || null,
                actionRequired: editContractForm.actionRequired || null,
                numberOfExtensions: editContractForm.numberOfExtensions !== '' ? Number(editContractForm.numberOfExtensions) : null,
                finalExtensionPossible: editContractForm.finalExtensionPossible || null
              };
              const updated = await updateContractRenewalFeedback(editingContractFeedbackId, body);
              setContractFeedbacks(prev => prev.map(fb => getFeedbackId(fb) === editingContractFeedbackId ? updated : fb));
              setEditingContractFeedbackId(null);
              setEditContractForm({ contractId: '', managerFeedback: '', projectManagerFeedback: '', hrFeedback: '', actionRequired: '', numberOfExtensions: '', finalExtensionPossible: '' });
              alert('Feedback updated successfully!');
            } catch (err) {
              console.error('Failed to update feedback:', err);
              const errMsg = err.response?.data?.message || err.response?.data || err.message || '';
              alert(`Failed to update feedback: ${typeof errMsg === 'string' && errMsg ? errMsg : 'Unknown error.'}`);
            } finally {
              setIsSavingContractFeedback(false);
            }
          };

          const handleEditFeedback = (fb) => {
            const fbId = getFeedbackId(fb);
            setEditingContractFeedbackId(fbId);
            setEditContractForm({
              contractId: fb.contractId || '',
              managerFeedback: fb.managerFeedback || '',
              projectManagerFeedback: fb.projectManagerFeedback || '',
              hrFeedback: fb.hrFeedback || '',
              actionRequired: fb.actionRequired || '',
              numberOfExtensions: fb.numberOfExtensions != null ? fb.numberOfExtensions : '',
              finalExtensionPossible: fb.finalExtensionPossible || ''
            });
          };

          const handleDeleteFeedback = async (id) => {
            if (!id || !window.confirm('Are you sure you want to delete this contract feedback record?')) return;
            try {
              await deleteContractRenewalFeedback(id);
              setContractFeedbacks(prev => prev.filter(fb => getFeedbackId(fb) !== id));
              alert('Feedback deleted successfully!');
            } catch (err) {
              console.error('Failed to delete feedback:', err);
              alert('Failed to delete feedback record.');
            }
          };

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {/* Form Card - Always visible for NEW submissions */}
              <div style={{ backgroundColor: dm.card, borderRadius: '1rem', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem', color: dm.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <MessageSquare size={24} color="#3B82F6" />
                  New Contract Renewal Feedback
                </h2>
                <form onSubmit={handleSubmitFeedback}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Select Contract</label>
                      <select
                        value={contractRenewalForm.contractId}
                        onChange={(e) => handleContractChange(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          backgroundColor: dm.card,
                          color: dm.textPrimary
                        }}
                      >
                        {empContracts.length === 0 ? (
                          <option value="">No contracts found for employee</option>
                        ) : (
                          <>
                            <option value="">-- Select a contract --</option>
                            {empContracts.map(c => (
                              <option key={c.contractId || c.id} value={c.contractId || c.id}>
                                {c.type} Contract ({formatDateText(c.startDate)} - {c.endDate ? formatDateText(c.endDate) : 'Present'})
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Max Ext.</label>
                      <input
                        type="number"
                        min="0"
                        value={contractRenewalForm.numberOfExtensions}
                        onChange={(e) => setContractRenewalForm(prev => ({ ...prev, numberOfExtensions: e.target.value }))}
                        placeholder="Max Extensions"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          backgroundColor: dm.card,
                          color: dm.textPrimary,
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Final Possible</label>
                      <input
                        type="text"
                        value={contractRenewalForm.finalExtensionPossible}
                        onChange={(e) => setContractRenewalForm(prev => ({ ...prev, finalExtensionPossible: e.target.value }))}
                        placeholder="e.g. CDI"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          backgroundColor: dm.card,
                          color: dm.textPrimary,
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Manager Feedback</label>
                      <RichTextEditor
                        value={contractRenewalForm.managerFeedback || ''}
                        onChange={(val) => setContractRenewalForm(prev => ({ ...prev, managerFeedback: val }))}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Project Manager Feedback</label>
                      <RichTextEditor
                        value={contractRenewalForm.projectManagerFeedback || ''}
                        onChange={(val) => setContractRenewalForm(prev => ({ ...prev, projectManagerFeedback: val }))}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>HR Feedback</label>
                      <RichTextEditor
                        value={contractRenewalForm.hrFeedback || ''}
                        onChange={(val) => setContractRenewalForm(prev => ({ ...prev, hrFeedback: val }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Action Required</label>
                      <RichTextEditor
                        value={contractRenewalForm.actionRequired || ''}
                        onChange={(val) => setContractRenewalForm(prev => ({ ...prev, actionRequired: val }))}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={isSavingContractFeedback}
                      style={{ padding: '0.5rem 1.25rem', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', opacity: isSavingContractFeedback ? 0.6 : 1 }}
                    >
                      {isSavingContractFeedback ? 'Saving...' : 'Submit Feedback'}
                    </button>
                  </div>
                </form>
              </div>

              {/* List Card */}
              <div style={{ backgroundColor: dm.card, borderRadius: '1rem', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem', color: dm.textPrimary }}>Contract Evaluation History</h2>
                {contractFeedbacks.length === 0 ? (
                  <p style={{ color: dm.textSecondary, fontSize: '0.875rem', fontStyle: 'italic' }}>No renewal evaluations submitted for this employee's contracts yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {contractFeedbacks.map(fb => {
                      const fbId = getFeedbackId(fb);
                      return (
                        <div key={fbId || Math.random()} style={{ border: `1px solid ${dm.border}`, borderRadius: '0.75rem', padding: '1.25rem', backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#F9FAFB' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.875rem', color: dm.textPrimary }}>
                              Contract Period: {formatDateText(fb.contractStartDate)} to {fb.contractEndDate ? formatDateText(fb.contractEndDate) : 'Present'} ({fb.contractType || 'N/A'})
                              {fb.status && <span style={{ marginLeft: '8px', padding: '2px 6px', borderRadius: '4px', backgroundColor: fb.status === 'ACTIVE' ? '#D1FAE5' : '#FEE2E2', color: fb.status === 'ACTIVE' ? '#065F46' : '#991B1B', fontSize: '0.75rem' }}>{fb.status}</span>}
                            </span>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => handleEditFeedback(fb)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3B82F6', padding: '4px' }}
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteFeedback(fbId)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '4px' }}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>

                          {editingContractFeedbackId && editingContractFeedbackId === fbId ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                  <strong style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>Max Ext.</strong>
                                  <input
                                    type="number"
                                    min="0"
                                    value={editContractForm.numberOfExtensions}
                                    onChange={(e) => setEditContractForm(prev => ({ ...prev, numberOfExtensions: e.target.value }))}
                                    style={{ width: '100%', padding: '0.75rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: dm.card, color: dm.textPrimary, boxSizing: 'border-box' }}
                                  />
                                </div>
                                <div>
                                  <strong style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>Final Possible</strong>
                                  <input
                                    type="text"
                                    value={editContractForm.finalExtensionPossible}
                                    onChange={(e) => setEditContractForm(prev => ({ ...prev, finalExtensionPossible: e.target.value }))}
                                    style={{ width: '100%', padding: '0.75rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: dm.card, color: dm.textPrimary, boxSizing: 'border-box' }}
                                  />
                                </div>
                              </div>
                              <div>
                                <strong style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>Manager Feedback</strong>
                                <RichTextEditor
                                  value={editContractForm.managerFeedback || ''}
                                  onChange={(val) => setEditContractForm(prev => ({ ...prev, managerFeedback: val }))}
                                />
                              </div>
                              <div>
                                <strong style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>PM Feedback</strong>
                                <RichTextEditor
                                  value={editContractForm.projectManagerFeedback || ''}
                                  onChange={(val) => setEditContractForm(prev => ({ ...prev, projectManagerFeedback: val }))}
                                />
                              </div>
                              <div>
                                <strong style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>HR Feedback</strong>
                                <RichTextEditor
                                  value={editContractForm.hrFeedback || ''}
                                  onChange={(val) => setEditContractForm(prev => ({ ...prev, hrFeedback: val }))}
                                />
                              </div>
                              <div>
                                <strong style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>Action Required</strong>
                                <RichTextEditor
                                  value={editContractForm.actionRequired || ''}
                                  onChange={(val) => setEditContractForm(prev => ({ ...prev, actionRequired: val }))}
                                />
                              </div>
                              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingContractFeedbackId(null);
                                    setEditContractForm({ contractId: '', managerFeedback: '', projectManagerFeedback: '', hrFeedback: '', actionRequired: '', numberOfExtensions: '', finalExtensionPossible: '' });
                                  }}
                                  style={{ padding: '0.5rem 1.25rem', background: 'transparent', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', cursor: 'pointer', color: dm.textSecondary, fontWeight: 600, fontSize: '0.875rem' }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleUpdateFeedback}
                                  disabled={isSavingContractFeedback}
                                  style={{ padding: '0.5rem 1.25rem', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', opacity: isSavingContractFeedback ? 0.6 : 1 }}
                                >
                                  {isSavingContractFeedback ? 'Saving...' : 'Update Feedback'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                              <div style={{ backgroundColor: dm.card, padding: '0.75rem', borderRadius: '0.5rem', border: `1px solid ${dm.border}` }}>
                                <strong style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>Details</strong>
                                <div style={{ fontSize: '0.8rem', color: dm.textPrimary, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {fb.department && <div><span style={{ color: dm.textSecondary }}>Dept:</span> {fb.department}</div>}
                                  {fb.positions && <div><span style={{ color: dm.textSecondary }}>Positions:</span> {Array.isArray(fb.positions) ? fb.positions.join(', ') : fb.positions}</div>}
                                  {fb.projects && <div><span style={{ color: dm.textSecondary }}>Projects:</span> {Array.isArray(fb.projects) ? fb.projects.join(', ') : fb.projects}</div>}
                                  {fb.numberOfExtensions != null && <div><span style={{ color: dm.textSecondary }}>Max Extensions:</span> {fb.numberOfExtensions}</div>}
                                  {fb.finalExtensionPossible && <div><span style={{ color: dm.textSecondary }}>Final Possible:</span> {Array.isArray(fb.finalExtensionPossible) ? fb.finalExtensionPossible.join(', ') : fb.finalExtensionPossible}</div>}
                                </div>
                              </div>
                              <div style={{ backgroundColor: dm.card, padding: '0.75rem', borderRadius: '0.5rem', border: `1px solid ${dm.border}` }}>
                                <strong style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>Manager Feedback</strong>
                                <p style={{ fontSize: '0.875rem', color: dm.textPrimary, margin: 0, whiteSpace: 'pre-wrap' }}>{fb.managerFeedback || '-'}</p>
                              </div>
                              <div style={{ backgroundColor: dm.card, padding: '0.75rem', borderRadius: '0.5rem', border: `1px solid ${dm.border}` }}>
                                <strong style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>PM Feedback</strong>
                                <p style={{ fontSize: '0.875rem', color: dm.textPrimary, margin: 0, whiteSpace: 'pre-wrap' }}>{fb.projectManagerFeedback || '-'}</p>
                              </div>
                              <div style={{ backgroundColor: dm.card, padding: '0.75rem', borderRadius: '0.5rem', border: `1px solid ${dm.border}` }}>
                                <strong style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>HR Feedback & Action</strong>
                                <p style={{ fontSize: '0.875rem', color: dm.textPrimary, margin: 0, whiteSpace: 'pre-wrap' }}>
                                  <span style={{ display: 'block', marginBottom: '4px' }}><strong>Notes:</strong> {fb.hrFeedback || '-'}</span>
                                  <span style={{ display: 'block' }}><strong>Action Required:</strong> {fb.actionRequired || '-'}</span>
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {activeTab === 'bonuses' && employee && (() => {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {/* Form Card for Bonuses */}
              <div style={{ backgroundColor: dm.card, borderRadius: '1rem', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem', color: dm.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CreditCard size={24} color="#3B82F6" />
                  Add New Bonus
                </h2>
                <form onSubmit={handleSubmitBonus}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Bonus Type</label>
                      <select
                        value={bonusForm.type}
                        onChange={(e) => setBonusForm(prev => ({ ...prev, type: e.target.value }))}
                        style={{ width: '100%', padding: '0.75rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: dm.surface, color: dm.textPrimary }}
                        required
                      >
                        <option value="NOTICE_PAY">Indemnité de préavis (Notice Pay)</option>
                        <option value="SEVERANCE_PAY">Indemnité de licenciement (Severance Pay)</option>
                        <option value="OTHER">Autre (Other)</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={bonusForm.amount}
                        onChange={(e) => setBonusForm(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="Enter amount"
                        style={{ width: '100%', padding: '0.75rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: dm.surface, color: dm.textPrimary, boxSizing: 'border-box' }}
                        required
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Description (Optional)</label>
                      <input
                        type="text"
                        value={bonusForm.description}
                        onChange={(e) => setBonusForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="e.g. Indemnité préavis juin 2026"
                        style={{ width: '100%', padding: '0.75rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: dm.surface, color: dm.textPrimary, boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={isSavingBonus}
                      style={{ padding: '0.75rem 1.5rem', backgroundColor: '#10B981', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: isSavingBonus ? 0.6 : 1 }}
                    >
                      <Plus size={18} />
                      {isSavingBonus ? 'Saving...' : 'Add Bonus'}
                    </button>
                  </div>
                </form>
              </div>

              {/* List Card for Bonuses */}
              <div style={{ backgroundColor: dm.card, borderRadius: '1rem', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem', color: dm.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingUp size={24} color="#3B82F6" />
                  Bonuses History
                </h2>
                {!bonusesLoaded ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: dm.textSecondary }}>Loading bonuses...</div>
                ) : bonuses.length === 0 ? (
                  <div style={{ padding: '3rem', textAlign: 'center', color: dm.textSecondary, backgroundColor: dm.surface, borderRadius: '0.5rem', border: `1px dashed ${dm.border}` }}>
                    <CreditCard size={48} style={{ opacity: 0.2, margin: '0 auto 1rem auto', display: 'block' }} />
                    <p style={{ margin: 0 }}>No bonuses recorded yet.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {bonuses.map((bonus) => {
                      const isEditing = editingBonusId === bonus.id;
                      return (
                        <div key={bonus.id} style={{ backgroundColor: dm.surface, border: `1px solid ${dm.border}`, borderRadius: '0.75rem', padding: '1.5rem', transition: 'all 0.2s', position: 'relative' }}>
                          <div style={{ position: 'absolute', top: '1rem', right: '1rem', display: 'flex', gap: '0.5rem' }}>
                            {!isEditing && (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingBonusId(bonus.id);
                                    setEditBonusForm({
                                      description: bonus.description || '',
                                      type: bonus.type || 'NOTICE_PAY',
                                      amount: bonus.amount || ''
                                    });
                                  }}
                                  style={{ padding: '0.4rem', color: '#3B82F6', background: 'transparent', border: 'none', cursor: 'pointer' }}
                                  title="Edit Bonus"
                                >
                                  <Edit2 size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteBonus(bonus.id)}
                                  style={{ padding: '0.4rem', color: '#EF4444', background: 'transparent', border: 'none', cursor: 'pointer' }}
                                  title="Delete Bonus"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>

                          {isEditing ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                <div>
                                  <label style={{ display: 'block', fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.25rem' }}>Type</label>
                                  <select
                                    value={editBonusForm.type}
                                    onChange={(e) => setEditBonusForm(prev => ({ ...prev, type: e.target.value }))}
                                    style={{ width: '100%', padding: '0.5rem', border: `1px solid ${dm.border}`, borderRadius: '0.25rem', backgroundColor: dm.card, color: dm.textPrimary }}
                                  >
                                    <option value="NOTICE_PAY">NOTICE_PAY</option>
                                    <option value="SEVERANCE_PAY">SEVERANCE_PAY</option>
                                    <option value="OTHER">OTHER</option>
                                  </select>
                                </div>
                                <div>
                                  <label style={{ display: 'block', fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.25rem' }}>Amount</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editBonusForm.amount}
                                    onChange={(e) => setEditBonusForm(prev => ({ ...prev, amount: e.target.value }))}
                                    style={{ width: '100%', padding: '0.5rem', border: `1px solid ${dm.border}`, borderRadius: '0.25rem', backgroundColor: dm.card, color: dm.textPrimary, boxSizing: 'border-box' }}
                                  />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <label style={{ display: 'block', fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.25rem' }}>Description</label>
                                  <input
                                    type="text"
                                    value={editBonusForm.description}
                                    onChange={(e) => setEditBonusForm(prev => ({ ...prev, description: e.target.value }))}
                                    style={{ width: '100%', padding: '0.5rem', border: `1px solid ${dm.border}`, borderRadius: '0.25rem', backgroundColor: dm.card, color: dm.textPrimary, boxSizing: 'border-box' }}
                                  />
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  onClick={() => setEditingBonusId(null)}
                                  style={{ padding: '0.4rem 1rem', background: 'transparent', border: `1px solid ${dm.border}`, borderRadius: '0.25rem', color: dm.textSecondary, cursor: 'pointer' }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleUpdateBonus}
                                  disabled={isSavingBonus}
                                  style={{ padding: '0.4rem 1rem', background: '#3B82F6', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', opacity: isSavingBonus ? 0.6 : 1 }}
                                >
                                  Update
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                              <div>
                                <span style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block' }}>Type</span>
                                <span style={{ fontWeight: 600, color: dm.textPrimary }}>{bonus.type}</span>
                              </div>
                              <div>
                                <span style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block' }}>Amount</span>
                                <span style={{ fontWeight: 600, color: '#10B981' }}>{Number(bonus.amount).toFixed(2)}</span>
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block' }}>Description</span>
                                <span style={{ color: dm.textPrimary }}>{bonus.description || '-'}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {activeTab === 'overtime' && employee && (() => {
          const totalHours = overtimes.reduce((sum, item) => sum + (item.hours || 0), 0);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {/* Top Stats & Add Form Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                {/* Stat Box */}
                <div style={{
                  backgroundColor: dm.card, borderRadius: '1rem', padding: '2rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column',
                  justifyContent: 'center', alignItems: 'center', border: `1px solid ${dm.border}`
                }}>
                  <div style={{
                    width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#EFF6FF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem'
                  }}>
                    <Timer size={32} color="#3B82F6" />
                  </div>
                  <h3 style={{ fontSize: '1rem', color: dm.textSecondary, margin: '0 0 0.5rem 0', fontWeight: '600' }}>Total Overtime</h3>
                  <p style={{ fontSize: '2.5rem', fontWeight: '800', color: '#3B82F6', margin: 0 }}>{totalHours.toFixed(1)} <span style={{ fontSize: '1rem', fontWeight: '600', color: dm.textSecondary }}>Hours</span></p>
                </div>

                {/* Add Form Box */}
                <div style={{ backgroundColor: dm.card, borderRadius: '1rem', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: `1px solid ${dm.border}`, flex: 2 }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem', color: dm.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Plus size={20} color="#10B981" />
                    Log New Overtime
                  </h2>
                  <form onSubmit={handleSubmitOvertime}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Date</label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            value={overtimeForm.date}
                            onChange={(v) => setOvertimeForm(prev => ({ ...prev, date: v }))}
                            format="dd-MM-yyyy"
                            slotProps={{ textField: { size: 'small', sx: { width: '100%', bgcolor: dm.surface, borderRadius: '0.5rem', '& fieldset': { borderColor: dm.border } } } }}
                          />
                        </LocalizationProvider>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Hours</label>
                        <input
                          type="number"
                          step="0.5"
                          min="0.5"
                          value={overtimeForm.hours}
                          onChange={(e) => setOvertimeForm(prev => ({ ...prev, hours: e.target.value }))}
                          style={{ width: '100%', padding: '0.6rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: dm.surface, color: dm.textPrimary, boxSizing: 'border-box' }}
                          required
                        />
                      </div>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textSecondary, marginBottom: '0.5rem' }}>Description / Work Log</label>
                      <input
                        type="text"
                        placeholder="Describe what work was accomplished..."
                        value={overtimeForm.description}
                        onChange={(e) => setOvertimeForm(prev => ({ ...prev, description: e.target.value }))}
                        style={{ width: '100%', padding: '0.6rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: dm.surface, color: dm.textPrimary, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="submit"
                        disabled={isSavingOvertime}
                        style={{ padding: '0.6rem 1.5rem', backgroundColor: '#10B981', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: isSavingOvertime ? 0.6 : 1 }}
                      >
                        {isSavingOvertime ? 'Saving...' : 'Log Overtime'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* History List Card */}
              <div style={{ backgroundColor: dm.card, borderRadius: '1rem', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: `1px solid ${dm.border}` }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem', color: dm.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingUp size={24} color="#3B82F6" />
                  Overtime Log History
                </h2>
                {!overtimesLoaded ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: dm.textSecondary }}>Loading overtime records...</div>
                ) : overtimes.length === 0 ? (
                  <div style={{ padding: '3rem', textAlign: 'center', color: dm.textSecondary, backgroundColor: dm.surface, borderRadius: '0.5rem', border: `1px dashed ${dm.border}` }}>
                    <Timer size={48} style={{ opacity: 0.2, margin: '0 auto 1rem auto', display: 'block' }} />
                    <p style={{ margin: 0 }}>No overtime hours logged for this employee.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {overtimes.map((record) => {
                      const isEditing = editingOvertimeId === record.id;
                      return (
                        <div key={record.id} style={{ backgroundColor: dm.surface, border: `1px solid ${dm.border}`, borderRadius: '0.75rem', padding: '1.5rem', position: 'relative' }}>
                          <div style={{ position: 'absolute', top: '1rem', right: '1rem', display: 'flex', gap: '0.5rem' }}>
                            {!isEditing && (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingOvertimeId(record.id);
                                    setEditOvertimeForm({
                                      date: record.startTime ? new Date(record.startTime) : null,
                                      hours: record.hours ? record.hours : ((new Date(record.endTime) - new Date(record.startTime)) / (1000 * 60 * 60)).toFixed(2),
                                      description: record.description || ''
                                    });
                                  }}
                                  style={{ padding: '0.4rem', color: '#3B82F6', background: 'transparent', border: 'none', cursor: 'pointer' }}
                                  title="Edit Overtime Record"
                                >
                                  <Edit2 size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteOvertime(record.id)}
                                  style={{ padding: '0.4rem', color: '#EF4444', background: 'transparent', border: 'none', cursor: 'pointer' }}
                                  title="Delete Overtime Record"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>

                          {isEditing ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                <div>
                                  <label style={{ display: 'block', fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.25rem' }}>Date</label>
                                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                      value={editOvertimeForm.date}
                                      onChange={(v) => setEditOvertimeForm(prev => ({ ...prev, date: v }))}
                                      format="dd-MM-yyyy"
                                      slotProps={{ textField: { size: 'small', sx: { width: '100%', bgcolor: dm.card, borderRadius: '0.25rem', '& fieldset': { borderColor: dm.border } } } }}
                                    />
                                  </LocalizationProvider>
                                </div>
                                <div>
                                  <label style={{ display: 'block', fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.25rem' }}>Hours</label>
                                  <input
                                    type="number"
                                    step="0.5"
                                    min="0.5"
                                    value={editOvertimeForm.hours}
                                    onChange={(e) => setEditOvertimeForm(prev => ({ ...prev, hours: e.target.value }))}
                                    style={{ width: '100%', padding: '0.5rem', border: `1px solid ${dm.border}`, borderRadius: '0.25rem', backgroundColor: dm.card, color: dm.textPrimary, boxSizing: 'border-box' }}
                                  />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <label style={{ display: 'block', fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.25rem' }}>Description</label>
                                  <input
                                    type="text"
                                    value={editOvertimeForm.description}
                                    onChange={(e) => setEditOvertimeForm(prev => ({ ...prev, description: e.target.value }))}
                                    style={{ width: '100%', padding: '0.5rem', border: `1px solid ${dm.border}`, borderRadius: '0.25rem', backgroundColor: dm.card, color: dm.textPrimary, boxSizing: 'border-box' }}
                                  />
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  onClick={() => setEditingOvertimeId(null)}
                                  style={{ padding: '0.4rem 1rem', background: 'transparent', border: `1px solid ${dm.border}`, borderRadius: '0.25rem', color: dm.textSecondary, cursor: 'pointer' }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleUpdateOvertime}
                                  disabled={isSavingOvertime}
                                  style={{ padding: '0.4rem 1rem', background: '#3B82F6', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', opacity: isSavingOvertime ? 0.6 : 1 }}
                                >
                                  Update
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                              <div>
                                <span style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block' }}>Date</span>
                                <span style={{ fontWeight: 600, color: dm.textPrimary }}>
                                  {formatDate(record.startTime)}
                                </span>
                              </div>
                              <div>
                                <span style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block' }}>Hours</span>
                                <span style={{ color: dm.textSecondary }}>
                                  {Number(record.hours).toFixed(2)} hrs
                                </span>
                              </div>
                              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <span style={{ fontSize: '0.75rem', color: dm.textSecondary, display: 'block', fontWeight: '600', textTransform: 'uppercase' }}>Description</span>
                                  <span style={{ color: dm.textPrimary, display: 'block', marginTop: '0.25rem', fontSize: '0.9rem' }}>
                                    {record.description || '-'}
                                  </span>
                                </div>
                                <div>
                                  {record.confirmed === true ? (
                                    <span style={{ backgroundColor: '#D1FAE5', color: '#065F46', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '600' }}>Approved</span>
                                  ) : record.confirmed === false ? (
                                    <span style={{ backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '600' }}>Rejected</span>
                                  ) : (
                                    <span style={{ backgroundColor: '#FEF3C7', color: '#92400E', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '600' }}>Pending</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
      <AbsenceRecordsModal
        open={showSuspensionModal}
        onClose={() => setShowSuspensionModal(false)}
        employeeId={employee?.id}
        absenceType="SUSPENSION"
        employeeName={employee?.name || 'N/A'}
        selectedYear={selectedYear} // ✅ Pass the year
      />
      {/* --- ABSENCE ASSESSMENT MODAL --- */}
      <Dialog
        open={showAssessmentModal}
        onClose={() => setShowAssessmentModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" fontWeight="700" color="#111827">
                {selectedAbsenceForAssessment && absenceViolationsMap[selectedAbsenceForAssessment.id]
                  ? 'Edit Absence Assessment'
                  : 'Add Absence Assessment'
                }
              </Typography>
              {selectedAbsenceForAssessment && (
                <Typography variant="body2" color="#6B7280" sx={{ mt: 0.5 }}>
                  <strong>{selectedAbsenceForAssessment.ty_pe}</strong> &nbsp;·&nbsp; {formatDate(selectedAbsenceForAssessment.startDate || selectedAbsenceForAssessment.date)}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => setShowAssessmentModal(false)} sx={{ color: '#9CA3AF' }}>
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* Violation Description */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                <AlertCircle size={14} style={{ display: 'inline', marginRight: '6px', color: '#EF4444' }} />
                Violation Description *
              </label>
              <RichTextEditor
                value={assessmentForm.violationDescription || ''}
                onChange={(val) => setAssessmentForm(prev => ({ ...prev, violationDescription: val }))}
              />
            </div>

            {/* Manager Review */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                <UserCheck size={14} style={{ display: 'inline', marginRight: '6px', color: '#3B82F6' }} />
                Manager Review
              </label>
              <RichTextEditor
                value={assessmentForm.managerReview || ''}
                onChange={(val) => setAssessmentForm(prev => ({ ...prev, managerReview: val }))}
              />
            </div>

            {/* Employee Reaction */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                <MessageSquare size={14} style={{ display: 'inline', marginRight: '6px', color: '#8B5CF6' }} />
                Employee Reaction
              </label>
              <RichTextEditor
                value={assessmentForm.employeeReaction || ''}
                onChange={(val) => setAssessmentForm(prev => ({ ...prev, employeeReaction: val }))}
              />
            </div>

            {/* Employee Agreement */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                <MessageSquare size={14} style={{ display: 'inline', marginRight: '6px', color: '#F59E0B' }} />
                Employee Agreement / Action
              </label>
              <RichTextEditor
                value={assessmentForm.employeeAgreement || ''}
                onChange={(val) => setAssessmentForm(prev => ({ ...prev, employeeAgreement: val }))}
              />
            </div>

          </div>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setShowAssessmentModal(false)}
            sx={{ color: '#6B7280', textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssessmentSubmit}
            variant="contained"
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: '8px',
              backgroundColor: '#3B82F6', boxShadow: 'none',
              '&:hover': { backgroundColor: '#2563EB', boxShadow: '0 2px 8px rgba(59,130,246,0.35)' }
            }}
          >
            {selectedAbsenceForAssessment && absenceViolationsMap[selectedAbsenceForAssessment.id]
              ? 'Update Assessment'
              : 'Save Assessment'
            }
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default ViewProfile;
